import { ClientLoggingService } from '../../../../shared/client-logging.service';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  Output,
  EventEmitter,
  HostBinding,
  NgZone,
  OnChanges,
  OnDestroy,
  Renderer2,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { animate, style, transition, trigger } from '@angular/animations';
import { MatSidenavContainer, MatSidenavModule } from '@angular/material/sidenav';
import { BreakpointObserver } from '@angular/cdk/layout';
import { MatListModule } from '@angular/material/list';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { HANDSET_DIALOG_QUERY, HandsetDialogService } from '../../../../shared/handset-dialog.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { ArchitecturalPlanPage } from '../../../../shared/models/architectural-plan-page.model';

import {
  SelectedPagesToolbarComponent,
  SelectedPagesToolbarAction,
  SelectedPageRef,
} from './selected-pages-toolbar/selected-pages-toolbar.component';
import {
  PageIngestionDialogComponent,
  PageIngestionDialogData,
} from '../../settings/page-ingestion-dialog/page-ingestion-dialog.component';
import {
  InputFileMetadata,
  RegionHierarchyTreeNode,
  GetRegionHierarchyResponse,
} from '../../../../../generated.commonjs/api_pb';
import { extractFileIdFromPage } from '../../../../shared/page-path.util';
import { FileMetadataService, ProcessingStatus } from '../../../../shared/file-metadata.service';
import { PlanReviewerService } from '../../../../shared/plan-reviewer.service';
import {
  FileComplianceCoverageState,
  FilePageNavigationStatus,
} from '../../../../../generated.commonjs/plan.reviewer_pb';
import { Subscription } from 'rxjs';
import { TruncatedTooltipDirective } from './truncated-tooltip.directive';
import { calculateSelectedPageVisualOffset } from './sticky-page-offset';

/**
 * Represents a group of pages from a single file.
 * Issue #167: Used for hierarchical TOC display.
 */
interface FileGroup {
  fileId: string;
  fileName: string;
  metadata: any;
  pages: ArchitecturalPlanPage[];
  expanded: boolean;
  statusText: string;
  statusClass: string;
  progress: number;
}

type TreeItemKind = 'project' | 'file' | 'page' | 'region';
type SemanticStickyScrollUnderOwner = 'project' | 'file';

interface PageWarningMarker {
  risk: 'high' | 'medium';
  count: number;
  className: string;
  label: string;
}

/** True only while content crosses the currently exposed lower edge of the visual sticky stack. */
export function hasStickyScrollUnderOverlap(
  exposedBottom: number,
  rows: readonly Pick<DOMRect, 'top' | 'bottom'>[]
): boolean {
  if (!Number.isFinite(exposedBottom)) return false;
  return rows.some(
    row =>
      Number.isFinite(row.top) &&
      Number.isFinite(row.bottom) &&
      row.top < exposedBottom - 0.5 &&
      row.bottom > exposedBottom + 0.5
  );
}

/** Lazily measures rows until one crosses the exposed sticky edge. */
export function hasStickyScrollUnderElementOverlap(
  exposedBottom: number,
  rows: readonly Pick<HTMLElement, 'getBoundingClientRect'>[]
): boolean {
  if (!Number.isFinite(exposedBottom)) return false;
  return rows.some(row => hasStickyScrollUnderOverlap(exposedBottom, [row.getBoundingClientRect()]));
}

/** Hides the upper sticky prefix when it cannot share the viewport with the lower ancestry stack. */
export function hasTopAndBottomStickyCollision(
  viewportHeight: number,
  topStackHeight: number,
  bottomStackHeight: number
): boolean {
  return (
    Number.isFinite(viewportHeight) &&
    Number.isFinite(topStackHeight) &&
    Number.isFinite(bottomStackHeight) &&
    viewportHeight > 0 &&
    topStackHeight > 0 &&
    bottomStackHeight > 0 &&
    viewportHeight + 0.5 < topStackHeight + bottomStackHeight
  );
}

/**
 * Keeps a semantic active row represented while it remains below the unobscured lower edge.
 *
 * Descendant projections consume space above the physical viewport edge. Their ancestors must
 * join the stack as soon as they cross that raised boundary; otherwise an ancestor briefly paints
 * underneath its descendant before the next projection is added.
 */
export function shouldPinActiveTreeRowToBottom(
  viewport: Pick<DOMRect, 'top' | 'bottom'> | null | undefined,
  activeRow: Pick<DOMRect, 'top' | 'bottom' | 'height'> | null | undefined,
  projectedDescendantHeight = 0
): boolean {
  if (!viewport || !activeRow) return false;
  const handoffTolerance = projectedDescendantHeight > 0 ? 0 : 0.5;
  return (
    [viewport.top, viewport.bottom, activeRow.top, activeRow.bottom, activeRow.height, projectedDescendantHeight].every(
      Number.isFinite
    ) &&
    viewport.bottom > viewport.top &&
    activeRow.height > 0 &&
    projectedDescendantHeight >= 0 &&
    activeRow.bottom > viewport.bottom - projectedDescendantHeight + handoffTolerance
  );
}

/**
 * The subset of `PageIngestionDialogComponent`'s close payload the drawer acts on.
 *
 * The dialog closes with `PageIngestionDialogResult` plus dispatch details
 * (`started`, `taskCount`, `taskIds`); only `action` and `started` matter here.
 */
interface ReingestDialogResult {
  action?: 'ingest' | 'cancel';
  started?: boolean;
}

interface BottomProjectionSource {
  item: HTMLElement;
  row: HTMLElement;
  height?: number;
}

/** Side navigation drawer that lists plan pages as thumbnails and supports multi-select, drag-reorder, and page actions. */
@Component({
  selector: 'app-drawer',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './drawer.component.html',
  styleUrls: ['./drawer.component.css'],
  imports: [
    CommonModule,
    FormsModule,
    MatSidenavModule,
    MatListModule,
    MatCheckboxModule,
    MatIconModule,
    MatChipsModule,
    MatProgressBarModule,
    MatButtonModule,
    MatTooltipModule,
    TruncatedTooltipDirective,
    SelectedPagesToolbarComponent,
  ],
  animations: [
    trigger('regionExpand', [
      transition(':enter', [
        style({ height: '0', opacity: 0, overflow: 'hidden' }),
        animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)', style({ height: '*', opacity: 1 })),
      ]),
      transition(':leave', [
        style({ overflow: 'hidden' }),
        animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)', style({ height: '0', opacity: 0 })),
      ]),
    ]),
  ],
})
export class DrawerComponent implements OnChanges, OnDestroy {
  @ViewChild(MatSidenavContainer) private sidenavContainer!: MatSidenavContainer;
  @ViewChild('drawerListScroll')
  private set drawerListScroll(ref: ElementRef<HTMLElement> | undefined) {
    this.detachBottomActiveProjectionHost();
    this.drawerListScrollElement = ref?.nativeElement ?? null;
    if (this.drawerListScrollElement) this.attachBottomActiveProjectionHost(this.drawerListScrollElement);
    this.observeStickyHandoffGeometry();
  }

  @ViewChild('projectTree')
  private set projectTree(ref: ElementRef<HTMLElement> | undefined) {
    this.projectTreeElement = ref?.nativeElement ?? null;
    this.observeStickyHandoffGeometry();
  }

  private cdr = inject(ChangeDetectorRef);
  private breakpointObserver = inject(BreakpointObserver);
  private dialog = inject(MatDialog);
  private handsetDialog = inject(HandsetDialogService);
  private fileMetadataService = inject(FileMetadataService);
  private planReviewerService = inject(PlanReviewerService);
  private router = inject(Router);
  private snackBar = inject(MatSnackBar);
  private ngZone = inject(NgZone);
  private renderer = inject(Renderer2);

  @Input() opened = true;
  @Input() pages: ArchitecturalPlanPage[] = [];
  @Input() activePageNumber: number | null = null;
  @Input() activeFileId: string | null = null;
  @Input() planId: string = '';
  @Input() projectName: string = '';
  /** Project settings/tasks are a selected tree node, not an absent page selection. */
  @Input() projectSelected = false;
  @Input() loading = false;
  /**
   * Checked pages keyed by the id of the file they belong to.
   *
   * Issue #1652: this used to be a bare `Set<number>`, which carried no file
   * identity — bulk re-ingestion then had to guess a file and always picked
   * the first one returned by `getProjectFileMetadata()`. Keying by fileId
   * keeps every checked page bound to its own file.
   */
  @Input() checkedPagesByFile: Map<string, Set<number>> = new Map();
  @Input() powerUserMode = false;
  @Input() activeRegionSlug: string | null = null;
  /** Enables file-label selection only for an explicit #2225 consumer. */
  @Input() fileLabelActivatesFile = false;
  @Input() filePageStatuses = new Map<number, FilePageNavigationStatus.AsObject>();
  /** Cosmetic preference; operational coverage statuses remain visible when false. */
  @Input() showComplianceWarnings = true;
  @Output() openedChange = new EventEmitter<boolean>();
  @Output() pageActivated = new EventEmitter<{ page: ArchitecturalPlanPage; fileId: string }>();
  @Output() fileActivated = new EventEmitter<{ fileId: string }>();
  @Output() regionActivated = new EventEmitter<{
    page: ArchitecturalPlanPage;
    fileId: string;
    region: RegionHierarchyTreeNode.AsObject;
  }>();
  /**
   * The region named by the address bar was found in the page's hierarchy (Issue #2445).
   *
   * A region link, or a reload with one in the address bar, selects the region's row here. The
   * Preview needs the region itself, with its box, to highlight it on the page; before this output
   * existed only a click supplied it, so a reload dropped the highlight. Unlike `regionActivated`
   * this reports a selection that is already in the address bar, so it must not navigate.
   */
  @Output() regionRestored = new EventEmitter<RegionHierarchyTreeNode.AsObject>();
  @Output() regionHovered = new EventEmitter<RegionHierarchyTreeNode.AsObject | null>();
  @Output() checkedPagesChanged = new EventEmitter<Map<string, Set<number>>>();

  selectedPages: SelectedPageRef[] = [];
  isHandset = false;

  // Issue #167: Hierarchical file structure for TOC
  fileGroups: FileGroup[] = [];
  loadingFileMetadata = false;
  projectExpanded = true;
  focusedTreeItemKey = 'project';
  private readonly reducedMotionQuery = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)') ?? null;
  @HostBinding('class.reduced-motion') prefersReducedMotion = this.reducedMotionQuery?.matches ?? false;
  projectCollapseAnimating = false;
  projectTreeAnimating = false;
  projectChildrenVisuallyExpanded = true;
  private projectExpansionFrameId: number | null = null;
  readonly collapsingFileIds = new Set<string>();
  readonly animatingFileIds = new Set<string>();
  readonly reversingFileIds = new Set<string>();
  readonly reconcilingFileIds = new Set<string>();
  private fileReversalGeneration = 0;
  private readonly activeFileReversals = new Map<
    string,
    {
      generation: number;
      wrapper: HTMLElement;
      fileGroup: FileGroup;
      transitions: readonly CSSTransition[];
      finishedPromisesFulfilled: boolean;
      endedProperties: Set<string>;
      completionStarted: boolean;
      onTransitionEnd: (event: TransitionEvent) => void;
      onTransitionCancel: (event: TransitionEvent) => void;
    }
  >();
  stickyHandoffSpacePx = 0;
  pinnedRegionAncestorCount = 0;
  stickyAncestorStackVisible = false;
  stickyStackScrollUnder = false;
  semanticStickyScrollUnderOwner: SemanticStickyScrollUnderOwner | null = null;
  bottomActiveProjectionVisible = false;
  bottomActiveProjectionHeightPx = 0;
  private drawerListScrollElement: HTMLElement | null = null;
  private projectTreeElement: HTMLElement | null = null;
  private selectedPageVisualOwner: HTMLElement | null = null;
  private selectedPageVisualOffsetPx = 0;
  private stickyStackOverlapFrameId: number | null = null;
  private drawerTreeScrollEndTimer: ReturnType<typeof setTimeout> | null = null;
  private bottomActiveOverlayElement: HTMLElement | null = null;
  private bottomActiveProjectionSources: BottomProjectionSource[] = [];
  private bottomActiveProjectionFrameId: number | null = null;
  private bottomActiveProjectionClickCleanup: (() => void) | null = null;
  private bottomActiveProjectionGestureCleanups: (() => void)[] = [];
  private bottomActiveProjectionTouchY: number | null = null;
  private stickyTopBottomCollision = false;
  private readonly collapsedStickyRegionCounts = new Map<string, number>();
  private stickyHandoffFrameId: number | null = null;
  private readonly stickyHandoffResizeObserver =
    typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(() => {
          this.scheduleStickyHandoffSpaceMeasurement();
          this.scheduleBottomActiveProjectionUpdate();
        });
  private pendingActiveRevealKey: string | null = null;
  private readonly onReducedMotionChange = (event: MediaQueryListEvent): void => {
    this.prefersReducedMotion = event.matches;
    if (event.matches) {
      this.cancelProjectExpansionFrame();
      this.projectChildrenVisuallyExpanded = this.projectExpanded;
      this.projectTreeAnimating = false;
      this.projectCollapseAnimating = false;
      this.collapsingFileIds.clear();
      this.animatingFileIds.clear();
      this.cancelActiveFileReversals();
      this.reversingFileIds.clear();
    }
    this.scheduleStickyHandoffSpaceMeasurement();
    this.cdr.markForCheck();
  };

  // Issue #1228: Region hierarchy per page (Power User Mode)
  /**
   * Memoizes the folder-path -> fileId parse for the template-bound
   * `isPageChecked()` (Issue #1652). Keyed by page object so entries are
   * garbage-collected with the pages themselves.
   */
  private parsedFileIdCache = new WeakMap<ArchitecturalPlanPage, string | null>();
  /** Region cache keys per page and file id; see `regionCacheKey()`. */
  private readonly regionCacheKeys = new WeakMap<ArchitecturalPlanPage, Map<string, string>>();
  /** Plain-object copies of each cached region response; see `getRegionNodes()`. */
  private readonly regionNodeObjects = new WeakMap<RegionHierarchyTreeNode[], RegionHierarchyTreeNode.AsObject[]>();
  /** Page positions per file page list; see `pageIndexesFor()`. */
  private readonly pageIndexes = new WeakMap<ArchitecturalPlanPage[], Map<number, number>>();
  private static readonly NO_REGION_NODES: RegionHierarchyTreeNode.AsObject[] = [];

  regionHierarchyCache = new Map<string, RegionHierarchyTreeNode[]>();
  expandedRegionPages = new Set<string>();
  loadingRegionHierarchy = new Set<string>();
  expandedRegionNodes = new Set<string>();
  activeRegionPath: string | null = null;

  /**
   * Selection key for pages whose folder path carries no file id (legacy
   * single-file projects). Deliberately not a valid file id — see
   * `resolveFileIdForPage()`.
   */
  private static readonly UNRESOLVED_FILE_ID = '__unresolved__';

  /** NCS discipline designator lookup for sheet ID prefixes. */
  private static readonly DISCIPLINE_MAP: Record<string, string> = {
    G: 'General',
    H: 'Hazardous Materials',
    V: 'Survey / Mapping',
    B: 'Geotechnical',
    W: 'Civil Works',
    C: 'Civil',
    L: 'Landscape',
    S: 'Structural',
    A: 'Architectural',
    I: 'Interiors',
    Q: 'Equipment',
    F: 'Fire Protection',
    P: 'Plumbing',
    D: 'Process',
    M: 'Mechanical',
    E: 'Electrical',
    T: 'Telecommunications',
    R: 'Resource',
    X: 'Other Disciplines',
    Z: 'Contractor / Shop Drawings',
    O: 'Operations',
  };

  // Resizable drawer state
  drawerWidth = 300;
  private isResizing = false;
  private static readonly MIN_WIDTH = 200;
  private static readonly MAX_WIDTH = 600;
  private static readonly LS_KEY_WIDTH = 'toc_drawer_width';
  private static readonly SS_KEY_EXPANSION_PREFIX = 'toc_tree_expansion:';
  private readonly breakpointSubscription: Subscription;
  private readonly onResizeMouseMove = (event: MouseEvent): void => {
    if (!this.isResizing) {
      return;
    }

    const newWidth = event.clientX;
    this.drawerWidth = Math.max(DrawerComponent.MIN_WIDTH, Math.min(DrawerComponent.MAX_WIDTH, newWidth));
    this.cdr.detectChanges();
    this.sidenavContainer?.updateContentMargins();
  };
  private readonly onResizeMouseUp = (): void => {
    this.stopResize();
    localStorage.setItem(DrawerComponent.LS_KEY_WIDTH, this.drawerWidth.toString());
  };

  constructor() {
    this.reducedMotionQuery?.addEventListener?.('change', this.onReducedMotionChange);

    const saved = localStorage.getItem(DrawerComponent.LS_KEY_WIDTH);
    if (saved) {
      const parsedWidth = parseInt(saved, 10);
      if (!Number.isNaN(parsedWidth)) {
        this.drawerWidth = Math.max(DrawerComponent.MIN_WIDTH, Math.min(DrawerComponent.MAX_WIDTH, parsedWidth));
      }
    }

    this.breakpointSubscription = this.breakpointObserver.observe(HANDSET_DIALOG_QUERY).subscribe(({ matches }) => {
      this.isHandset = matches;
      if (matches) {
        this.closeDrawerOnHandset();
      }
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.stopResize();
    this.cancelProjectExpansionFrame();
    this.cancelStickyHandoffSpaceMeasurement();
    this.cancelStickyStackOverlapMeasurement();
    this.cancelDrawerTreeScrollEndTimer();
    this.cancelBottomActiveProjectionUpdate();
    this.detachBottomActiveProjectionHost();
    this.stickyHandoffResizeObserver?.disconnect();
    this.breakpointSubscription.unsubscribe();
    this.reducedMotionQuery?.removeEventListener?.('change', this.onReducedMotionChange);
    this.cancelActiveFileReversals();
  }

  startResize(event: MouseEvent): void {
    event.preventDefault();
    if (this.isResizing) {
      return;
    }

    this.isResizing = true;
    document.addEventListener('mousemove', this.onResizeMouseMove);
    document.addEventListener('mouseup', this.onResizeMouseUp);
  }

  private stopResize(): void {
    if (!this.isResizing) {
      return;
    }

    this.isResizing = false;
    document.removeEventListener('mousemove', this.onResizeMouseMove);
    document.removeEventListener('mouseup', this.onResizeMouseUp);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['activeRegionSlug']) {
      this.activeRegionPath = changes['activeRegionSlug'].currentValue;
      this.collapsedStickyRegionCounts.clear();
      this.pinnedRegionAncestorCount = 0;
      this.updateStickyAncestorStackVisibility(false);
      this.requestActiveRegionReveal();
      this.scheduleBottomActiveProjectionUpdate();
    }

    if (changes['powerUserMode'] && changes['powerUserMode'].currentValue) {
      this.requestActiveRegionReveal();
    }

    // Update selected pages whenever the pages input changes
    if (changes['pages']) {
      this.updateSelectedPages();

      // NEW (Issue #167): Group pages by files when pages change
      // Don't await - let it run async to avoid blocking Angular change detection
      if (this.planId && this.pages.length > 0) {
        this.groupPagesByFiles();
      }
    }

    // Reload file groups when planId changes (project switch)
    if (changes['planId']) {
      this.restoreExpansionState();
      if (!changes['planId'].firstChange) {
        ClientLoggingService.log(
          'INFO',
          '[DrawerComponent] Plan ID changed from',
          changes['planId'].previousValue,
          'to',
          changes['planId'].currentValue
        );
        // Clear existing file groups to show loading state
        this.fileGroups = [];
        // Reload file groups for the new project
        if (this.planId && this.pages.length > 0) {
          this.groupPagesByFiles();
        }
      }
    }

    if (changes['activePageNumber'] || changes['activeFileId'] || changes['projectSelected']) {
      this.collapsedStickyRegionCounts.clear();
      this.pinnedRegionAncestorCount = 0;
      this.updateStickyAncestorStackVisibility(false);
      this.requestActivePageReveal();
      this.requestActiveRegionReveal();
      this.scheduleStickyHandoffSpaceMeasurement();
      this.scheduleBottomActiveProjectionUpdate();
    }

    if (changes['checkedPagesByFile']) {
      this.bottomActiveProjectionSources = [];
      this.scheduleBottomActiveProjectionUpdate();
    }
  }

  get projectDisplayName(): string {
    return this.projectName.trim() || this.planId || 'Project';
  }

  get totalTreePageCount(): number {
    return this.fileGroups.reduce((total, group) => total + group.pages.length, 0);
  }

  get activeTreeFile(): FileGroup | undefined {
    return this.fileGroups.find(group => group.fileId === this.activeFileId);
  }

  get activeTreePage(): ArchitecturalPlanPage | undefined {
    return this.activeTreeFile?.pages.find(page => page.getPageNumber() === this.activePageNumber);
  }

  /**
   * Gets the display name for a document type from the metadata object.
   * Issue #167: Used for the file subtitle.
   */
  getDocumentTypeForFile(metadata: any): string {
    // metadata is from toObject(), so we access properties directly
    const docTypeEnum = metadata.documentType;
    return this.fileMetadataService.formatDocumentType(docTypeEnum);
  }

  formatPageCount(pageCount: number): string {
    return `${pageCount} ${pageCount === 1 ? 'page' : 'pages'}`;
  }

  getFileSubtitle(fileGroup: FileGroup): string {
    return `${this.getDocumentTypeForFile(fileGroup.metadata)} · ${this.formatPageCount(fileGroup.pages.length)}`;
  }

  /**
   * Groups pages by their source files for hierarchical display.
   * Issue #167: Creates expandable file sections in TOC.
   */
  private async groupPagesByFiles(): Promise<void> {
    try {
      this.loadingFileMetadata = true;

      // Fetch file metadata for the project
      const response = await this.fileMetadataService.listFileMetadata(this.planId).toPromise();
      const fileMetadataList = response?.getFilesList() || [];

      ClientLoggingService.log('INFO', '[DrawerComponent] Loaded file metadata:', fileMetadataList.length, 'files');
      ClientLoggingService.log('INFO', '[DrawerComponent] Total pages to group:', this.pages.length);

      // Create file groups
      this.fileGroups = fileMetadataList
        .map(metadata => {
          const fileId = metadata.getFileId();
          const fileName = metadata.getFileName();
          const extractedPages = metadata.getExtractedPagesList();
          ClientLoggingService.log(
            'DEBUG',
            `[DrawerComponent] File ${fileId} (${fileName}) has ${extractedPages.length} extracted pages`
          );

          // Find pages that belong to this file by extracting file ID from page_folder_path
          // Modern structure: projects/{project_id}/files/{file_id}/pages/{page_number}/
          // Legacy structure: projects/{project_id}/pages/{page_number}/
          //
          // This predicate runs once per page for every file (thousands of times on a large
          // project). It used to log each page it checked with console.debug, which cost
          // main-thread time on every grouping even with DevTools closed (Issue #3304; the
          // logging had already moved off ClientLoggingService for #738). Pages left out of every
          // file are counted and reported once per grouping instead.
          const filePages = this.pages.filter(page => {
            const pageFolderPath = page.getPageFolderPath();
            const modernPathMatch = pageFolderPath.match(/\/files\/(\d+)\/pages\/\d+\/?$/);
            if (modernPathMatch) {
              return modernPathMatch[1] === fileId;
            }

            // For legacy structure, fall back to the old logic (all pages belong to "virtual" file)
            // This ensures backward compatibility with single-file projects
            const isLegacyPath = !pageFolderPath.includes('/files/') && /\/pages\/\d+\/?$/.test(pageFolderPath);
            if (isLegacyPath) {
              // For legacy projects, group all pages under the first file
              return fileMetadataList[0] === metadata;
            }
            return false;
          });

          ClientLoggingService.log(
            'INFO',
            '[DrawerComponent] File',
            fileId,
            '(' + fileName + ') matched',
            filePages.length,
            'pages'
          );

          const metadataObject = metadata.toObject();
          const totalPages = Number(metadataObject.pageCount) || filePages.length;
          const completedPages = metadataObject.extractedPagesList?.length ?? filePages.length;
          const isCompleted = metadataObject.processingStatus === ProcessingStatus.PROCESSING_STATUS_COMPLETED;

          return {
            fileId,
            fileName,
            metadata: metadataObject,
            pages: filePages,
            expanded: this.shouldFileStartExpanded(fileId),
            statusText: this.fileMetadataService.formatProcessingStatus(metadataObject.processingStatus),
            statusClass: this.fileMetadataService.getStatusColorClass(metadataObject.processingStatus),
            // A Completed status is authoritative. Historical metadata can have an incomplete
            // extracted-pages list even though processing reached its terminal success state;
            // exposing that ratio as a partial progressbar contradicts the status.
            progress: isCompleted
              ? 100
              : totalPages > 0
                ? Math.min(100, Math.round((completedPages / totalPages) * 100))
                : 0,
          };
        })
        .filter(group => group.pages.length > 0); // Only show files with pages

      ClientLoggingService.log('INFO', '[DrawerComponent] Created', this.fileGroups.length, 'file groups with pages');
      const groupedPages = this.fileGroups.reduce((count, group) => count + group.pages.length, 0);
      if (groupedPages < this.pages.length) {
        ClientLoggingService.log(
          'INFO',
          `[DrawerComponent] ${this.pages.length - groupedPages} of ${this.pages.length} pages are in no file group`
        );
      }
      this.ensureActivePageAncestorsExpanded();
      this.requestActiveRegionReveal();
    } catch (error) {
      ClientLoggingService.log(
        'ERROR',
        `[DrawerComponent] Error grouping pages by files:` +
          ` code=${(error as any)?.code}, message=${(error as any)?.message}`,
        error
      );
      // Fall back to flat list on error
      this.fileGroups = [];
    } finally {
      this.loadingFileMetadata = false;
      // The groups arrive after an await, outside any template event or input change.
      this.cdr.markForCheck();
      this.scheduleStickyHandoffSpaceMeasurement();
    }
  }

  /**
   * Adds only the terminal range an already-overflowing tree needs for its
   * final file row to reach the sticky file slot. A tree whose natural content
   * fits must never gain an artificial scroll runway.
   */
  private calculateStickyHandoffSpace(
    naturalScrollHeight: number,
    clientHeight: number,
    projectRowHeight: number,
    finalFileBranchHeight: number
  ): number {
    if (
      ![naturalScrollHeight, clientHeight, projectRowHeight, finalFileBranchHeight].every(Number.isFinite) ||
      naturalScrollHeight <= clientHeight + 1
    ) {
      return 0;
    }
    return Math.max(0, clientHeight - projectRowHeight - finalFileBranchHeight);
  }

  private observeStickyHandoffGeometry(): void {
    this.stickyHandoffResizeObserver?.disconnect();
    if (this.drawerListScrollElement) this.stickyHandoffResizeObserver?.observe(this.drawerListScrollElement);
    if (this.projectTreeElement) this.stickyHandoffResizeObserver?.observe(this.projectTreeElement);
    this.scheduleStickyHandoffSpaceMeasurement();
    this.scheduleBottomActiveProjectionUpdate();
  }

  private scheduleStickyHandoffSpaceMeasurement(): void {
    this.cancelStickyHandoffSpaceMeasurement();
    this.stickyHandoffFrameId = this.requestMeasurementFrame(() => {
      this.stickyHandoffFrameId = null;
      this.updateStickyHandoffSpace();
    });
  }

  /**
   * Schedules a layout measurement for the next frame outside Angular's zone. A frame scheduled
   * inside the zone makes Angular check the whole app when it ends, even when the measurement
   * changed nothing, and these measurements run on every resize, scroll and expansion (Issue
   * #2997). A measurement that does change something either re-renders the drawer itself with
   * detectChanges or calls markForCheck, which schedules a check from outside the zone.
   */
  private requestMeasurementFrame(callback: FrameRequestCallback): number {
    return this.ngZone.runOutsideAngular(() => requestAnimationFrame(callback));
  }

  private cancelStickyHandoffSpaceMeasurement(): void {
    if (this.stickyHandoffFrameId === null) return;
    cancelAnimationFrame(this.stickyHandoffFrameId);
    this.stickyHandoffFrameId = null;
  }

  private updateStickyHandoffSpace(): void {
    const scroll = this.drawerListScrollElement;
    const tree = this.projectTreeElement;
    if (!scroll || !tree || this.projectTreeAnimating || this.animatingFileIds.size > 0) return;

    const projectRow = tree.querySelector<HTMLElement>(':scope > .project-root > .project-root-row');
    const fileBranches = tree.querySelectorAll<HTMLElement>('.project-children-content > .file-treeitem');
    const finalFileBranch = fileBranches.item(fileBranches.length - 1);
    if (!projectRow || !finalFileBranch) {
      if (this.stickyHandoffSpacePx !== 0) {
        this.stickyHandoffSpacePx = 0;
        this.cdr.detectChanges();
      }
      this.onDrawerTreeScroll();
      return;
    }

    const naturalScrollHeight = Math.max(0, scroll.scrollHeight - this.stickyHandoffSpacePx);
    const nextSpace = this.calculateStickyHandoffSpace(
      naturalScrollHeight,
      scroll.clientHeight,
      projectRow.getBoundingClientRect().height,
      finalFileBranch.getBoundingClientRect().height
    );
    if (Math.abs(nextSpace - this.stickyHandoffSpacePx) <= 0.5) {
      this.onDrawerTreeScroll();
      return;
    }

    this.stickyHandoffSpacePx = nextSpace;
    this.cdr.detectChanges();
    this.onDrawerTreeScroll();
  }

  toggleProject(event?: Event): void {
    event?.stopPropagation();
    const wrapper = this.findProjectChildrenWrapper(event);
    const wasExpanded = this.projectExpanded;
    const isReversingLeave =
      !wasExpanded && !this.prefersReducedMotion && this.projectCollapseAnimating && wrapper !== null;

    if (isReversingLeave) {
      // This is a connected wrapper with an in-flight CSS transition. Retarget
      // that transition in the same event turn so the browser reverses from its
      // current interpolated grid track. Fresh-mount staging and a separate
      // inline-height animation both introduce a second motion owner and can
      // move the wrapper before an external observer reads the first frame.
      this.cancelProjectExpansionFrame();
      this.projectExpanded = true;
      this.projectTreeAnimating = true;
      this.projectCollapseAnimating = false;
      this.projectChildrenVisuallyExpanded = true;
      this.persistExpansionState();
      this.synchronizeStickyExpansionProjection('project', false);
      return;
    }

    this.projectExpanded = !this.projectExpanded;
    this.startProjectTreeTransition();
    this.persistExpansionState();
    this.synchronizeStickyExpansionProjection('project', wasExpanded);
  }

  private findProjectChildrenWrapper(event?: Event): HTMLElement | null {
    const currentTarget = event?.currentTarget;
    const eventWrapper =
      currentTarget instanceof Element
        ? (currentTarget
            .closest<HTMLElement>('.project-root')
            ?.querySelector<HTMLElement>(':scope > .project-children') ?? null)
        : null;
    const wrapper =
      eventWrapper ??
      this.projectTreeElement?.querySelector<HTMLElement>(':scope > .project-root > .project-children') ??
      null;
    return wrapper?.isConnected ? wrapper : null;
  }

  private startProjectTreeTransition(): void {
    this.cancelProjectExpansionFrame();
    if (this.prefersReducedMotion) {
      this.projectChildrenVisuallyExpanded = this.projectExpanded;
      this.projectTreeAnimating = false;
      this.projectCollapseAnimating = false;
      return;
    }

    this.projectTreeAnimating = true;
    this.projectCollapseAnimating = !this.projectExpanded;
    if (!this.projectExpanded) {
      this.projectChildrenVisuallyExpanded = false;
      return;
    }

    // Keep the newly mounted wrapper at its collapsed geometry for one paint.
    // Applying the expanded class on the following frame gives the browser a
    // real 0fr start state and makes rapid leave/enter reversals reuse the same
    // node instead of snapping to the subtree's natural height.
    this.projectChildrenVisuallyExpanded = false;
    this.projectExpansionFrameId = requestAnimationFrame(() => {
      this.projectExpansionFrameId = requestAnimationFrame(() => {
        this.projectExpansionFrameId = null;
        if (!this.projectExpanded || this.prefersReducedMotion) return;
        this.projectChildrenVisuallyExpanded = true;
        this.cdr.detectChanges();
      });
    });
  }

  private cancelProjectExpansionFrame(): void {
    if (this.projectExpansionFrameId === null) return;
    cancelAnimationFrame(this.projectExpansionFrameId);
    this.projectExpansionFrameId = null;
  }

  openProjectSettings(event?: Event): void {
    event?.stopPropagation();
    void this.router.navigate(['/projects', this.planId, 'settings', 'overview']).then(navigated => {
      if (navigated) {
        this.closeDrawerOnHandset();
      }
    });
  }

  toggleFile(fileGroup: FileGroup, event?: Event): void {
    event?.stopPropagation();
    const wrapper = this.findFileChildrenWrapper(fileGroup.fileId, event);
    const wasExpanded = fileGroup.expanded;
    const activeReversal = this.activeFileReversals.get(fileGroup.fileId);

    if (wasExpanded && activeReversal) {
      if (wrapper !== activeReversal.wrapper || !activeReversal.wrapper.isConnected) return;
      // A second toggle returns the same browser-owned transitions toward the
      // collapsed endpoint. Retire this generation before changing semantic
      // state so its eventual terminal callbacks cannot complete a newer motion.
      activeReversal.transitions.forEach(transition => transition.reverse());
      this.detachFileReversalListeners(activeReversal);
      this.activeFileReversals.delete(fileGroup.fileId);
      this.reversingFileIds.delete(fileGroup.fileId);
    }
    const isReversingLeave =
      !wasExpanded && !this.prefersReducedMotion && this.collapsingFileIds.has(fileGroup.fileId) && wrapper !== null;

    if (isReversingLeave) {
      // Reverse the three connected, browser-owned leave transitions before
      // semantic expansion. The explicit reversal preserves their current
      // interpolated values as the reversed transition's start
      // values; a class-only retarget can otherwise advance to a later layout
      // frame between an external sample and this event handler.
      const transitions = this.findFileLeaveTransitions(wrapper);
      if (transitions === null) return;
      transitions.forEach(transition => transition.reverse());
      // Capture the reversed transitions' current finished promises now. A
      // later getter can describe a different play-state generation after CSS
      // reconciliation. Promise fulfillment and transitionend dispatch may
      // arrive in either order, so completion waits for both below.
      const finishedTransitions = transitions.map(transition => transition.finished);
      const generation = ++this.fileReversalGeneration;
      const onTransitionEnd = (transitionEvent: TransitionEvent): void => {
        if (
          transitionEvent.target !== wrapper ||
          !['grid-template-rows', 'opacity', 'transform'].includes(transitionEvent.propertyName)
        ) {
          return;
        }
        const active = this.activeFileReversals.get(fileGroup.fileId);
        if (active?.generation !== generation || active.wrapper !== wrapper) return;
        active.endedProperties.add(transitionEvent.propertyName);
        this.tryCompleteFileReversal(fileGroup.fileId, generation);
      };
      const onTransitionCancel = (transitionEvent: TransitionEvent): void => {
        if (
          transitionEvent.target === wrapper &&
          ['grid-template-rows', 'opacity', 'transform'].includes(transitionEvent.propertyName)
        ) {
          this.cancelFileReversal(fileGroup.fileId, generation);
        }
      };
      wrapper.addEventListener('transitionend', onTransitionEnd);
      wrapper.addEventListener('transitioncancel', onTransitionCancel);
      this.activeFileReversals.set(fileGroup.fileId, {
        generation,
        wrapper,
        fileGroup,
        transitions,
        finishedPromisesFulfilled: false,
        endedProperties: new Set<string>(),
        completionStarted: false,
        onTransitionEnd,
        onTransitionCancel,
      });
      this.reversingFileIds.add(fileGroup.fileId);
      fileGroup.expanded = true;
      this.collapsingFileIds.delete(fileGroup.fileId);
      this.animatingFileIds.add(fileGroup.fileId);
      this.persistExpansionState();
      this.synchronizeStickyExpansionProjection(`file:${fileGroup.fileId}`, false);
      void Promise.all(finishedTransitions).then(
        () => {
          const active = this.activeFileReversals.get(fileGroup.fileId);
          if (active?.generation !== generation) return;
          active.finishedPromisesFulfilled = true;
          this.tryCompleteFileReversal(fileGroup.fileId, generation);
        },
        () => this.cancelFileReversal(fileGroup.fileId, generation)
      );
      return;
    }

    fileGroup.expanded = !fileGroup.expanded;
    if (fileGroup.expanded) {
      this.collapsingFileIds.delete(fileGroup.fileId);
    } else if (this.prefersReducedMotion) {
      this.collapsingFileIds.delete(fileGroup.fileId);
    } else {
      this.collapsingFileIds.add(fileGroup.fileId);
    }
    if (this.prefersReducedMotion) {
      this.animatingFileIds.delete(fileGroup.fileId);
    } else {
      this.animatingFileIds.add(fileGroup.fileId);
    }
    this.persistExpansionState();
    this.synchronizeStickyExpansionProjection(`file:${fileGroup.fileId}`, wasExpanded);
  }

  private findFileLeaveTransitions(wrapper: HTMLElement): readonly CSSTransition[] | null {
    if (typeof CSSTransition === 'undefined' || typeof wrapper.getAnimations !== 'function') return null;
    const expectedProperties = ['grid-template-rows', 'opacity', 'transform'] as const;
    const transitions = new Map<(typeof expectedProperties)[number], CSSTransition[]>();
    expectedProperties.forEach(property => transitions.set(property, []));

    const animations = wrapper.getAnimations();
    if (animations.length !== expectedProperties.length) return null;
    for (const animation of animations) {
      if (!(animation instanceof CSSTransition)) return null;
      const property = animation.transitionProperty as (typeof expectedProperties)[number];
      if (!transitions.has(property)) return null;
      const timing = animation.effect?.getTiming();
      const currentTime = animation.currentTime;
      if (
        timing?.duration !== 150 ||
        timing.easing !== 'ease-in' ||
        (!animation.pending && animation.playState !== 'running') ||
        typeof currentTime !== 'number' ||
        !Number.isFinite(currentTime) ||
        currentTime < 0 ||
        currentTime > 150
      ) {
        return null;
      }
      transitions.get(property)!.push(animation);
    }

    const ordered = expectedProperties.map(property => transitions.get(property)!);
    if (ordered.some(matches => matches.length !== 1)) return null;
    return ordered.map(([transition]) => transition);
  }

  private tryCompleteFileReversal(fileId: string, generation: number): void {
    const active = this.activeFileReversals.get(fileId);
    if (active?.generation !== generation || active.completionStarted || !active.finishedPromisesFulfilled) return;
    const expectedProperties = ['grid-template-rows', 'opacity', 'transform'];
    if (
      active.endedProperties.size !== expectedProperties.length ||
      expectedProperties.some(property => !active.endedProperties.has(property))
    ) {
      return;
    }
    active.completionStarted = true;
    // Chromium can dispatch transitioncancel when transition:none is applied
    // synchronously inside the final transitionend listener, even though all
    // three end events and finished promises have completed. Leave the CSS
    // event-dispatch stack first; the generation check in completion makes a
    // second toggle or teardown before this microtask a safe no-op.
    queueMicrotask(() => this.completeFileReversal(fileId, generation));
  }

  private completeFileReversal(fileId: string, generation: number): void {
    const active = this.activeFileReversals.get(fileId);
    if (active?.generation !== generation) return;
    if (!active.wrapper.isConnected || !active.fileGroup.expanded || this.prefersReducedMotion) {
      this.cancelFileReversal(fileId, generation);
      return;
    }
    this.detachFileReversalListeners(active);
    this.activeFileReversals.delete(fileId);
    // The reversed transitions' captured finished promises have resolved at
    // the expanded visual endpoint while the durable class is still absent.
    // Disable transitions only now, after the originals are fully settled, so
    // the matching CSS endpoint can be committed without cancellation or a
    // replacement animation.
    this.reconcilingFileIds.add(fileId);
    this.reversingFileIds.delete(fileId);
    this.cdr.detectChanges();
    void active.wrapper.offsetHeight;
    this.reconcilingFileIds.delete(fileId);
    this.animatingFileIds.delete(fileId);
    this.cdr.detectChanges();
    this.scheduleStickyHandoffSpaceMeasurement();
  }

  private cancelFileReversal(fileId: string, generation: number): void {
    const active = this.activeFileReversals.get(fileId);
    if (active?.generation !== generation) return;
    this.detachFileReversalListeners(active);
    this.activeFileReversals.delete(fileId);
    this.reversingFileIds.delete(fileId);
    this.reconcilingFileIds.delete(fileId);
    this.animatingFileIds.delete(fileId);
    this.cdr.detectChanges();
    this.scheduleStickyHandoffSpaceMeasurement();
  }

  private cancelActiveFileReversals(): void {
    const activeReversals = [...this.activeFileReversals.values()];
    this.activeFileReversals.clear();
    this.reversingFileIds.clear();
    this.reconcilingFileIds.clear();
    activeReversals.forEach(active => {
      this.animatingFileIds.delete(active.fileGroup.fileId);
      this.detachFileReversalListeners(active);
      active.transitions.forEach(transition => transition.cancel());
    });
  }

  private detachFileReversalListeners(active: {
    wrapper: HTMLElement;
    onTransitionEnd: (event: TransitionEvent) => void;
    onTransitionCancel: (event: TransitionEvent) => void;
  }): void {
    active.wrapper.removeEventListener('transitionend', active.onTransitionEnd);
    active.wrapper.removeEventListener('transitioncancel', active.onTransitionCancel);
  }

  private findFileChildrenWrapper(fileId: string, event?: Event): HTMLElement | null {
    const currentTarget = event?.currentTarget;
    const eventWrapper =
      currentTarget instanceof Element
        ? (currentTarget
            .closest<HTMLElement>('.file-treeitem')
            ?.querySelector<HTMLElement>(':scope > .file-children') ?? null)
        : null;
    const escapedFileId = CSS.escape(fileId);
    const wrapper =
      eventWrapper ??
      this.projectTreeElement?.querySelector<HTMLElement>(
        `:scope > .project-root > .project-children [data-file-id="${escapedFileId}"] > .file-children`
      ) ??
      null;
    return wrapper?.isConnected ? wrapper : null;
  }

  onProjectTreeTransitionEnd(event: TransitionEvent): void {
    if (event.target !== event.currentTarget || event.propertyName !== 'grid-template-rows') return;
    if (this.projectChildrenVisuallyExpanded !== this.projectExpanded) return;
    this.projectTreeAnimating = false;
    this.projectCollapseAnimating = false;
    this.collapsingFileIds.clear();
    this.animatingFileIds.clear();
    this.cancelActiveFileReversals();
    this.scheduleStickyHandoffSpaceMeasurement();
  }

  onFileTreeTransitionEnd(fileGroup: FileGroup, event: TransitionEvent): void {
    if (event.target !== event.currentTarget || event.propertyName !== 'grid-template-rows') return;
    if (this.activeFileReversals.has(fileGroup.fileId)) return;
    if (!fileGroup.expanded) {
      this.collapsingFileIds.delete(fileGroup.fileId);
    }
    this.animatingFileIds.delete(fileGroup.fileId);
    this.scheduleStickyHandoffSpaceMeasurement();
  }

  /**
   * Keeps the label target compatible with #2225 without making disclosure and
   * selection the same action. A consumer must explicitly enable file
   * activation; merely observing the output must not disable disclosure.
   */
  onFileLabelClick(fileGroup: FileGroup, event: Event): void {
    event.stopPropagation();
    if (this.fileLabelActivatesFile) {
      this.fileActivated.emit({ fileId: fileGroup.fileId });
      this.closeDrawerOnHandset();
      return;
    }
    const wasExpanded = fileGroup.expanded;
    this.toggleFile(fileGroup);
    if (fileGroup.expanded !== wasExpanded) {
      this.cdr.detectChanges();
    }
  }

  onTreeItemFocus(key: string): void {
    this.focusedTreeItemKey = key;
  }

  isTreeItemFocused(key: string): boolean {
    return this.focusedTreeItemKey === key;
  }

  fileTreeKey(fileId: string): string {
    return `file:${fileId}`;
  }

  pageTreeKey(fileId: string, page: ArchitecturalPlanPage): string {
    return `page:${fileId}:${page.getPageNumber()}`;
  }

  pageStatus(page: ArchitecturalPlanPage, fileId: string): FilePageNavigationStatus.AsObject | undefined {
    return fileId === this.activeFileId ? this.filePageStatuses.get(page.getPageNumber()) : undefined;
  }

  pageWarningMarkers(status: FilePageNavigationStatus.AsObject): PageWarningMarker[] {
    if (!this.showComplianceWarnings) return [];
    const markers: PageWarningMarker[] = [];
    if (status.highRiskCount > 0) {
      markers.push({
        risk: 'high',
        count: status.highRiskCount,
        className: 'app-compliance-count-high page-warning-high',
        label: this.warningCountLabel(status.highRiskCount, 'High'),
      });
    }
    if (status.mediumRiskCount > 0) {
      markers.push({
        risk: 'medium',
        count: status.mediumRiskCount,
        className: 'app-compliance-count-medium page-warning-medium',
        label: this.warningCountLabel(status.mediumRiskCount, 'Medium'),
      });
    }
    return markers;
  }

  private warningCountLabel(count: number, risk: 'High' | 'Medium'): string {
    return `${count} ${risk} Risk compliance ${count === 1 ? 'result' : 'results'}`;
  }

  hasPageCoverageStatus(status: FilePageNavigationStatus.AsObject): boolean {
    return status.coverageState !== FileComplianceCoverageState.FILE_COMPLIANCE_COVERAGE_STATE_UNSPECIFIED;
  }

  pageCoverageShortLabel(status: FilePageNavigationStatus.AsObject): string {
    switch (status.coverageState) {
      case FileComplianceCoverageState.FILE_COMPLIANCE_COVERAGE_STATE_NOT_APPLICABLE:
        return 'N/A';
      case FileComplianceCoverageState.FILE_COMPLIANCE_COVERAGE_STATE_STALE:
        return 'Stale';
      case FileComplianceCoverageState.FILE_COMPLIANCE_COVERAGE_STATE_MISSING:
        return 'Missing';
      case FileComplianceCoverageState.FILE_COMPLIANCE_COVERAGE_STATE_PENDING:
        return 'Pending';
      case FileComplianceCoverageState.FILE_COMPLIANCE_COVERAGE_STATE_FAILED:
        return 'Failed';
      default:
        return 'Not run';
    }
  }

  pageCoverageLabel(status: FilePageNavigationStatus.AsObject): string {
    switch (status.coverageState) {
      case FileComplianceCoverageState.FILE_COMPLIANCE_COVERAGE_STATE_NOT_APPLICABLE:
        return 'Not applicable';
      case FileComplianceCoverageState.FILE_COMPLIANCE_COVERAGE_STATE_STALE:
        return 'Stale result';
      case FileComplianceCoverageState.FILE_COMPLIANCE_COVERAGE_STATE_MISSING:
        return 'Compliance not evaluated';
      case FileComplianceCoverageState.FILE_COMPLIANCE_COVERAGE_STATE_PENDING:
        return 'Compliance pending';
      case FileComplianceCoverageState.FILE_COMPLIANCE_COVERAGE_STATE_FAILED:
        return 'Compliance failed';
      default:
        return 'Compliance not evaluated';
    }
  }

  pageCoverageClass(status: FilePageNavigationStatus.AsObject): string {
    if (status.coverageState === FileComplianceCoverageState.FILE_COMPLIANCE_COVERAGE_STATE_STALE) {
      return 'page-status-stale';
    }
    if (status.coverageState === FileComplianceCoverageState.FILE_COMPLIANCE_COVERAGE_STATE_FAILED) {
      return 'page-status-failed';
    }
    return 'page-status-unevaluated';
  }

  pageStatusAriaSuffix(status: FilePageNavigationStatus.AsObject): string {
    const labels = this.pageWarningMarkers(status).map(marker => marker.label);
    if (this.hasPageCoverageStatus(status)) {
      labels.push(this.pageCoverageLabel(status));
    }
    return labels.length > 0 ? `, ${labels.join(', ')}` : '';
  }

  regionTreeKey(path: string): string {
    return `region:${path}`;
  }

  /**
   * Returns the selected region's ancestors in visual order. The template uses this read-only
   * projection for the sticky visual stack while the recursive tree remains the sole semantic
   * tree and interaction target.
   */
  getActiveRegionAncestors(page: ArchitecturalPlanPage, fileId: string): RegionHierarchyTreeNode.AsObject[] {
    const activePath = this.activeRegionPath;
    if (!activePath || fileId !== this.activeFileId || page.getPageNumber() !== this.activePageNumber) {
      return [];
    }

    const ancestors: RegionHierarchyTreeNode.AsObject[] = [];
    let candidates = this.getRegionNodes(page, fileId);
    while (candidates.length > 0) {
      const owner: RegionHierarchyTreeNode.AsObject | undefined = candidates.find(
        node => activePath === node.path || activePath.startsWith(`${node.path}/`)
      );
      if (!owner || activePath === owner.path) break;
      ancestors.push(owner);
      candidates = owner.childNodesList ?? [];
    }
    return ancestors;
  }

  /**
   * Returns the complete selected region chain, including the selected node. The zero-flow visual
   * stack consumes this projection; the original recursive rows remain the only semantic owners.
   */
  getActiveRegionStickyChain(page: ArchitecturalPlanPage, fileId: string): RegionHierarchyTreeNode.AsObject[] {
    const activePath = this.activeRegionPath;
    const activeFile = this.activeTreeFile;
    if (
      !activePath ||
      !this.projectExpanded ||
      !activeFile?.expanded ||
      fileId !== this.activeFileId ||
      page.getPageNumber() !== this.activePageNumber ||
      !this.isRegionExpanded(page, fileId)
    ) {
      return [];
    }

    const chain: RegionHierarchyTreeNode.AsObject[] = [];
    let candidates = this.getRegionNodes(page, fileId);
    while (candidates.length > 0) {
      const owner: RegionHierarchyTreeNode.AsObject | undefined = candidates.find(
        node => activePath === node.path || activePath.startsWith(`${node.path}/`)
      );
      if (!owner) break;
      chain.push(owner);
      if (activePath === owner.path || !this.isRegionNodeExpanded(owner, page, fileId)) break;
      candidates = owner.childNodesList ?? [];
    }
    return chain;
  }

  /**
   * Identifies selected-chain levels whose semantic node has a later sibling. Sticky clones cover
   * the semantic rows while scrolling, so they must repaint these neutral sibling spines instead
   * of hiding them behind the clones' opaque selected-path surfaces.
   */
  getActiveRegionStickySiblingRailDepths(
    page: ArchitecturalPlanPage,
    fileId: string,
    chain: RegionHierarchyTreeNode.AsObject[]
  ): number[] {
    const railDepths: number[] = [];
    for (let depth = 0; depth < chain.length; depth += 1) {
      const siblings = depth === 0 ? this.getRegionNodes(page, fileId) : (chain[depth - 1].childNodesList ?? []);
      const selectedIndex = siblings.findIndex(node => node.path === chain[depth].path);
      if (selectedIndex >= 0 && selectedIndex < siblings.length - 1) railDepths.push(depth);
    }
    return railDepths;
  }

  /** Number of still-visible semantic owners in the project/file/page sticky prefix. */
  getStickyBaseAncestorCount(fileGroup: FileGroup): number {
    if (!this.projectExpanded) return 1;
    return fileGroup.expanded ? 3 : 2;
  }

  /**
   * Reconciles the zero-flow sticky projection in the same turn as any disclosure change. Each
   * collapsed owner retains its own row while descendants disappear; reopening restores only the
   * region depth that was pinned before that owner collapsed.
   */
  private synchronizeStickyExpansionProjection(ownerKey: string, collapsing: boolean): void {
    if (collapsing) {
      this.collapsedStickyRegionCounts.set(ownerKey, this.pinnedRegionAncestorCount);
    }

    const activeFile = this.activeTreeFile;
    const activePage = this.activeTreePage;
    const visibleRegionCount =
      activeFile && activePage ? this.getActiveRegionStickyChain(activePage, activeFile.fileId).length : 0;
    const priorCount = collapsing
      ? this.pinnedRegionAncestorCount
      : (this.collapsedStickyRegionCounts.get(ownerKey) ?? this.pinnedRegionAncestorCount);
    if (!collapsing) this.collapsedStickyRegionCounts.delete(ownerKey);

    this.cancelStickyStackOverlapMeasurement();
    this.updatePinnedRegionAncestorCount(Math.min(priorCount, visibleRegionCount));
    this.resetStickyScrollUnderState();
    this.hideBottomActiveProjection(true);
  }

  /**
   * Suppresses hover paint while wheel, trackpad, or touch scrolling moves rows beneath a
   * stationary pointer. The short trailing window lets the browser settle hit testing before
   * hover feedback resumes on the row that is actually under the pointer.
   */
  onDrawerTreeScrollEvent(): void {
    this.cancelDrawerTreeScrollEndTimer();
    const tree = this.projectTreeElement;
    tree?.classList.add('tree-scrolling');
    this.ngZone.runOutsideAngular(() => {
      this.drawerTreeScrollEndTimer = setTimeout(() => {
        this.drawerTreeScrollEndTimer = null;
        tree?.classList.remove('tree-scrolling');
      }, 120);
    });
    this.onDrawerTreeScroll();
  }

  private cancelDrawerTreeScrollEndTimer(): void {
    if (this.drawerTreeScrollEndTimer !== null) {
      clearTimeout(this.drawerTreeScrollEndTimer);
      this.drawerTreeScrollEndTimer = null;
    }
    this.projectTreeElement?.classList.remove('tree-scrolling');
  }

  private attachBottomActiveProjectionHost(scroll: HTMLElement): void {
    const parent = scroll.parentElement;
    if (!parent) return;

    const host = this.renderer.createElement('div') as HTMLElement;
    this.renderer.addClass(host, 'project-tree');
    this.renderer.addClass(host, 'tree-bottom-active-overlay');
    this.renderer.setAttribute(host, 'data-testid', 'tree-bottom-active-overlay');
    this.renderer.setAttribute(host, 'aria-hidden', 'true');
    this.renderer.appendChild(parent, host);
    this.bottomActiveProjectionClickCleanup = this.renderer.listen(host, 'click', event =>
      this.onBottomActiveProjectionClick(event as MouseEvent)
    );
    this.bottomActiveProjectionGestureCleanups = [
      this.renderer.listen(host, 'wheel', event => this.forwardBottomProjectionWheel(event as WheelEvent, scroll)),
      this.renderer.listen(host, 'touchstart', event => this.startBottomProjectionTouch(event as TouchEvent)),
      this.renderer.listen(host, 'touchmove', event => this.forwardBottomProjectionTouch(event as TouchEvent, scroll)),
      this.renderer.listen(host, 'touchend', () => this.endBottomProjectionTouch()),
      this.renderer.listen(host, 'touchcancel', () => this.endBottomProjectionTouch()),
    ];
    this.bottomActiveOverlayElement = host;
    this.scheduleBottomActiveProjectionUpdate();
  }

  private detachBottomActiveProjectionHost(): void {
    this.cancelBottomActiveProjectionUpdate();
    this.bottomActiveProjectionClickCleanup?.();
    this.bottomActiveProjectionClickCleanup = null;
    for (const cleanup of this.bottomActiveProjectionGestureCleanups) cleanup();
    this.bottomActiveProjectionGestureCleanups = [];
    this.bottomActiveProjectionTouchY = null;
    const host = this.bottomActiveOverlayElement;
    host?.replaceChildren();
    if (host?.parentNode) this.renderer.removeChild(host.parentNode, host);
    this.bottomActiveOverlayElement = null;
    this.bottomActiveProjectionSources = [];
    this.bottomActiveProjectionVisible = false;
    this.bottomActiveProjectionHeightPx = 0;
  }

  private scheduleBottomActiveProjectionUpdate(): void {
    this.cancelBottomActiveProjectionUpdate();
    this.bottomActiveProjectionFrameId = this.requestMeasurementFrame(() => {
      this.bottomActiveProjectionFrameId = null;
      this.updateBottomActiveProjection();
    });
  }

  private cancelBottomActiveProjectionUpdate(): void {
    if (this.bottomActiveProjectionFrameId === null) return;
    cancelAnimationFrame(this.bottomActiveProjectionFrameId);
    this.bottomActiveProjectionFrameId = null;
  }

  private findActiveTreeProjectionChain(): BottomProjectionSource[] {
    const tree = this.projectTreeElement;
    if (!tree) return [];

    const chain: BottomProjectionSource[] = [];
    const append = (item: HTMLElement | null): void => {
      const row = item?.querySelector<HTMLElement>(':scope > .tree-node-row') ?? null;
      if (item && row) chain.push({ item, row });
    };

    const project = tree.querySelector<HTMLElement>(':scope > .project-root');
    append(project);
    if (this.projectSelected || !this.activeFileId) return chain;

    const file = tree.querySelector<HTMLElement>(`.file-treeitem[data-file-id="${CSS.escape(this.activeFileId)}"]`);
    append(file);
    if (this.activePageNumber === null || !file) return chain;

    const page = file.querySelector<HTMLElement>(`.page-treeitem[data-page-number="${this.activePageNumber}"]`);
    append(page ?? null);
    if (!this.activeRegionPath || !page) return chain;

    let currentPath = '';
    for (const part of this.activeRegionPath.split('/')) {
      currentPath = currentPath ? `${currentPath}/${part}` : part;
      append(page.querySelector<HTMLElement>(`.region-list-item[data-region-path="${CSS.escape(currentPath)}"]`));
    }
    return chain;
  }

  private updateBottomActiveProjection(viewportRect?: DOMRect): void {
    const scroll = this.drawerListScrollElement;
    const host = this.bottomActiveOverlayElement;
    const chain = this.findActiveTreeProjectionChain();
    if (!scroll || !host || chain.length === 0) {
      this.hideBottomActiveProjection(true);
      return;
    }

    viewportRect ??= scroll.getBoundingClientRect();
    const sources: Array<BottomProjectionSource & { height: number; rect: DOMRect }> = [];
    let projectedDescendantHeight = 0;
    for (let index = chain.length - 1; index >= 0; index -= 1) {
      const source = chain[index];
      if (!source) break;
      const rect = source.row.getBoundingClientRect();
      if (!shouldPinActiveTreeRowToBottom(viewportRect, rect, projectedDescendantHeight)) {
        break;
      }
      sources.push({ ...source, height: rect.height, rect });
      projectedDescendantHeight += rect.height;
    }
    sources.reverse();
    if (sources.length === 0) {
      this.hideBottomActiveProjection(false);
      return;
    }

    const needsRender =
      !this.bottomActiveProjectionVisible ||
      sources.length !== this.bottomActiveProjectionSources.length ||
      sources.some(
        (source, index) =>
          source.row !== this.bottomActiveProjectionSources[index]?.row ||
          source.height !== this.bottomActiveProjectionSources[index]?.height
      );
    if (needsRender) this.renderBottomActiveProjection(sources, host);
    this.bottomActiveProjectionSources = sources;
    this.bottomActiveProjectionVisible = true;
    this.bottomActiveProjectionHeightPx = sources.reduce((height, source) => height + source.height, 0);
    host.style.setProperty('--tree-bottom-active-stack-height', `${this.bottomActiveProjectionHeightPx}px`);
    host.classList.add('visible');
  }

  private forwardBottomProjectionWheel(event: WheelEvent, scroll: HTMLElement): void {
    if (event.ctrlKey || event.deltaY === 0) return;
    const lineHeight = Number.parseFloat(getComputedStyle(scroll).lineHeight) || 16;
    const multiplier =
      event.deltaMode === WheelEvent.DOM_DELTA_LINE
        ? lineHeight
        : event.deltaMode === WheelEvent.DOM_DELTA_PAGE
          ? scroll.clientHeight
          : 1;
    scroll.scrollTop += event.deltaY * multiplier;
    event.preventDefault();
  }

  private startBottomProjectionTouch(event: TouchEvent): void {
    this.bottomActiveProjectionTouchY = event.touches.length === 1 ? event.touches[0].clientY : null;
  }

  private forwardBottomProjectionTouch(event: TouchEvent, scroll: HTMLElement): void {
    const previousY = this.bottomActiveProjectionTouchY;
    if (previousY === null || event.touches.length !== 1) return;
    const nextY = event.touches[0].clientY;
    scroll.scrollTop += previousY - nextY;
    this.bottomActiveProjectionTouchY = nextY;
    event.preventDefault();
  }

  private endBottomProjectionTouch(): void {
    this.bottomActiveProjectionTouchY = null;
  }

  private renderBottomActiveProjection(
    sources: Array<BottomProjectionSource & { height: number }>,
    host: HTMLElement
  ): void {
    const clones = sources.map(({ item, row, height }, index) => {
      const itemClone = item.cloneNode(false) as HTMLElement;
      const rowClone = row.cloneNode(true) as HTMLElement;
      itemClone.classList.add('tree-bottom-active-item');
      itemClone.dataset['projectionIndex'] = String(index);
      itemClone.removeAttribute('role');
      itemClone.removeAttribute('tabindex');
      itemClone.removeAttribute('id');
      for (const attribute of Array.from(itemClone.attributes)) {
        if (attribute.name.startsWith('aria-')) itemClone.removeAttribute(attribute.name);
      }

      const depth = (
        row.style.getPropertyValue('--tree-node-depth') || item.style.getPropertyValue('--tree-node-depth')
      ).trim();
      if (depth) itemClone.style.setProperty('--tree-node-depth', depth);
      itemClone.style.setProperty('--tree-bottom-active-row-height', `${height}px`);
      itemClone.append(rowClone);
      for (const element of itemClone.querySelectorAll<HTMLElement>(
        '[id], [tabindex], button, input, select, textarea, a[href]'
      )) {
        element.removeAttribute('id');
        element.setAttribute('tabindex', '-1');
      }
      return itemClone;
    });
    host.replaceChildren(...clones);
    host.dataset['sourceTreeKey'] = sources.at(-1)?.item.dataset['treeKey'] ?? '';
  }

  private hideBottomActiveProjection(clearSource: boolean): void {
    this.bottomActiveProjectionVisible = false;
    this.bottomActiveProjectionHeightPx = 0;
    this.bottomActiveOverlayElement?.classList.remove('visible');
    this.bottomActiveOverlayElement?.style.setProperty('--tree-bottom-active-stack-height', '0px');
    if (!clearSource) return;
    this.bottomActiveProjectionSources = [];
    this.bottomActiveOverlayElement?.replaceChildren();
    this.bottomActiveOverlayElement?.removeAttribute('data-source-tree-key');
  }

  onBottomActiveProjectionClick(event: MouseEvent): void {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const clone = target.closest<HTMLElement>('.tree-bottom-active-item');
    const projectionIndex = Number(clone?.dataset['projectionIndex']);
    const sourceRow = Number.isInteger(projectionIndex)
      ? this.bottomActiveProjectionSources[projectionIndex]?.row
      : undefined;
    if (!sourceRow) return;

    event.preventDefault();
    event.stopPropagation();
    let sourceAction: HTMLElement | null = null;
    if (target.closest('.page-checkbox, .page-selection-control')) {
      sourceAction = sourceRow.querySelector<HTMLElement>('.page-checkbox input');
    } else if (target.closest('.tree-disclosure')) {
      sourceAction = sourceRow.querySelector<HTMLElement>(':scope > .tree-disclosure');
    } else if (target.closest('.project-label-action')) {
      sourceAction = sourceRow.querySelector<HTMLElement>('.project-label-action');
    } else if (target.closest('.file-label-action')) {
      sourceAction = sourceRow.querySelector<HTMLElement>('.file-label-action');
    }

    (sourceAction ?? sourceRow).click();
    this.bottomActiveProjectionSources = [];
    this.scheduleBottomActiveProjectionUpdate();
  }

  /**
   * Promotes crossed region ancestors into one page-owned zero-flow visual stack. Measuring the
   * original rows keeps the overlay synchronized through deep-scroll handoffs without making a
   * recursive branch establish the sticky containing block for its own descendants.
   */
  onDrawerTreeScroll(): void {
    const scroll = this.drawerListScrollElement;
    const tree = this.projectTreeElement;
    const scrollRect = scroll?.getBoundingClientRect();
    this.updateBottomActiveProjection(scrollRect);
    const activePage = tree?.querySelector<HTMLElement>('.page-treeitem.selected-sticky-page');
    if (!scroll || !scrollRect || !tree || !activePage) {
      this.resetSelectedPageVisualOffset(tree);
      this.updateStickyAncestorStackVisibility(false);
      this.updatePinnedRegionAncestorCount(0);
      this.resetStickyScrollUnderState();
      this.updateStickyOverlayBottomCollision(tree, false);
      return;
    }
    if (this.projectTreeAnimating || this.animatingFileIds.size > 0) {
      const activeFile = this.activeTreeFile;
      const activeTreePage = this.activeTreePage;
      const visibleRegionCount =
        activeFile && activeTreePage ? this.getActiveRegionStickyChain(activeTreePage, activeFile.fileId).length : 0;
      this.cancelStickyStackOverlapMeasurement();
      this.updatePinnedRegionAncestorCount(Math.min(this.pinnedRegionAncestorCount, visibleRegionCount));
      this.resetStickyScrollUnderState();
      this.updateStickyOverlayBottomCollision(tree, false);
      return;
    }

    const styles = getComputedStyle(tree);
    const treeRow = Number.parseFloat(styles.getPropertyValue('--tree-row-min-height'));
    if (!Number.isFinite(treeRow) || treeRow <= 0) {
      this.resetSelectedPageVisualOffset(tree);
      this.updateStickyAncestorStackVisibility(false);
      this.updatePinnedRegionAncestorCount(0);
      this.resetStickyScrollUnderState();
      this.updateStickyOverlayBottomCollision(tree, false);
      return;
    }

    const selectedPageHasCrossedStickySlot = this.updateSelectedPageVisualOffset(
      tree,
      activePage,
      treeRow,
      scrollRect.top
    );
    this.updateStickyAncestorStackVisibility(selectedPageHasCrossedStickySlot);
    const activeTreePage = this.activeTreePage;
    const activePageRegionsExpanded =
      !!activeTreePage && !!this.activeFileId && this.isRegionExpanded(activeTreePage, this.activeFileId);
    if (!selectedPageHasCrossedStickySlot || !this.activeRegionPath || !activePageRegionsExpanded) {
      this.updatePinnedRegionAncestorCount(0);
      this.updateStickyOverlayBottomCollision(
        tree,
        selectedPageHasCrossedStickySlot &&
          hasTopAndBottomStickyCollision(
            scrollRect.bottom - scrollRect.top,
            treeRow * 3,
            this.bottomActiveProjectionHeightPx
          )
      );
      this.scheduleStickyStackOverlapMeasurement(activePage);
      return;
    }

    const stackTop = scrollRect.top + treeRow * 3;
    const bottomProjectionReserve = this.bottomActiveProjectionHeightPx;
    const maxPinnedRegionCount = Math.max(
      0,
      Math.floor((scrollRect.bottom - stackTop - bottomProjectionReserve) / treeRow)
    );
    const ancestorRows = Array.from(
      activePage.querySelectorAll<HTMLElement>(
        '.region-list-item:is(.region-active-ancestor, .region-active) > .region-list-row'
      )
    );
    let pinnedCount = 0;
    for (const row of ancestorRows) {
      if (pinnedCount >= maxPinnedRegionCount) break;
      const targetTop = stackTop + pinnedCount * treeRow;
      if (row.getBoundingClientRect().top > targetTop + 0.5) break;
      pinnedCount += 1;
    }
    this.updatePinnedRegionAncestorCount(pinnedCount);
    this.updateStickyOverlayBottomCollision(
      tree,
      hasTopAndBottomStickyCollision(
        scrollRect.bottom - scrollRect.top,
        treeRow * (3 + pinnedCount),
        this.bottomActiveProjectionHeightPx
      )
    );
    this.scheduleStickyStackOverlapMeasurement(activePage);
  }

  private updateStickyOverlayBottomCollision(tree: HTMLElement | null, collides: boolean): void {
    this.stickyTopBottomCollision = collides;
    tree
      ?.querySelector<HTMLElement>('.tree-sticky-ancestor-overlay')
      ?.classList.toggle('bottom-active-collision', collides);
  }

  /** Measures the actual exposed edge after Angular has reconciled the requested sticky chain. */
  private scheduleStickyStackOverlapMeasurement(activePage: HTMLElement): void {
    this.cancelStickyStackOverlapMeasurement();
    this.stickyStackOverlapFrameId = this.requestMeasurementFrame(() => {
      this.stickyStackOverlapFrameId = null;
      if (!activePage.isConnected) {
        this.resetStickyScrollUnderState();
        return;
      }

      const overlay = this.projectTreeElement?.querySelector<HTMLElement>('.tree-sticky-ancestor-overlay');
      const exposedRow = overlay?.querySelector<HTMLElement>(':scope > .tree-sticky-clone:last-child');
      if (overlay?.isConnected && exposedRow) {
        const exposedBottom = exposedRow.getBoundingClientRect().bottom;
        const overlaps = hasStickyScrollUnderElementOverlap(
          exposedBottom,
          Array.from(activePage.querySelectorAll<HTMLElement>('.region-list-row'))
        );
        overlay.classList.toggle('bottom-active-collision', this.stickyTopBottomCollision);
        this.updateSemanticStickyScrollUnderOwner(null);
        this.updateStickyStackScrollUnder(overlaps);
        return;
      }

      this.updateStickyStackScrollUnder(false);
      const tree = this.projectTreeElement;
      const activeFile = activePage.closest<HTMLElement>('.file-treeitem');
      const fileRow = activeFile?.querySelector<HTMLElement>(':scope > .file-row');
      const pageRows = activeFile
        ? Array.from(activeFile.querySelectorAll<HTMLElement>('.page-treeitem > .drawer-list-row'))
        : [];
      if (fileRow && hasStickyScrollUnderElementOverlap(fileRow.getBoundingClientRect().bottom, pageRows)) {
        overlay?.classList.toggle('bottom-active-collision', this.stickyTopBottomCollision);
        this.updateSemanticStickyScrollUnderOwner('file');
        return;
      }

      const projectRow = tree?.querySelector<HTMLElement>('.project-root > .project-root-row');
      const fileRows = tree ? Array.from(tree.querySelectorAll<HTMLElement>('.file-treeitem > .file-row')) : [];
      const projectOverlaps =
        !!projectRow && hasStickyScrollUnderElementOverlap(projectRow.getBoundingClientRect().bottom, fileRows);
      overlay?.classList.toggle('bottom-active-collision', this.stickyTopBottomCollision);
      this.updateSemanticStickyScrollUnderOwner(projectOverlaps ? 'project' : null);
    });
  }

  private cancelStickyStackOverlapMeasurement(): void {
    if (this.stickyStackOverlapFrameId === null) return;
    cancelAnimationFrame(this.stickyStackOverlapFrameId);
    this.stickyStackOverlapFrameId = null;
  }

  private updateStickyStackScrollUnder(active: boolean): void {
    if (active === this.stickyStackScrollUnder) return;
    this.stickyStackScrollUnder = active;
    this.cdr.markForCheck();
  }

  private updateSemanticStickyScrollUnderOwner(owner: SemanticStickyScrollUnderOwner | null): void {
    if (owner === this.semanticStickyScrollUnderOwner) return;
    this.semanticStickyScrollUnderOwner = owner;
    this.cdr.markForCheck();
  }

  private resetStickyScrollUnderState(): void {
    this.cancelStickyStackOverlapMeasurement();
    this.updateStickyStackScrollUnder(false);
    this.updateSemanticStickyScrollUnderOwner(null);
  }

  /**
   * Keeps the original selected page row visually pinned after its semantic wrapper crosses the
   * page slot. A collapsed page wrapper is only one row tall, so native sticky positioning is
   * immediately constrained by that wrapper; this zero-flow translation supplies the missing
   * visual travel while preserving the wrapper as the sole treeitem and connector owner. The
   * returned projection state deliberately follows the wrapper's natural position rather than
   * the translated row: the row must yield at its file boundary, while the presentation-only
   * ancestry stack must remain visible as later sibling files scroll underneath it.
   */
  private updateSelectedPageVisualOffset(
    tree: HTMLElement,
    activePage: HTMLElement,
    projectFileRow: number,
    scrollTop: number
  ): boolean {
    const row = activePage.querySelector<HTMLElement>(':scope > .drawer-list-row');
    const activeFile = activePage.closest<HTMLElement>('.file-treeitem');
    if (!row || !activeFile) {
      this.resetSelectedPageVisualOffset(tree);
      return false;
    }

    if (this.selectedPageVisualOwner !== activePage) {
      this.selectedPageVisualOwner = activePage;
      this.selectedPageVisualOffsetPx = 0;
      tree.style.setProperty('--tree-selected-page-visual-offset', '0px');
    }

    const rowBox = row.getBoundingClientRect();
    const fileBox = activeFile.getBoundingClientRect();
    const slotTop = scrollTop + projectFileRow * 2;
    const selectedPageHasCrossedStickySlot = activePage.getBoundingClientRect().top <= slotTop + 0.5;
    const nextOffset = calculateSelectedPageVisualOffset({
      currentVisualTop: rowBox.top,
      currentOffset: this.selectedPageVisualOffsetPx,
      selectedSlotTop: slotTop,
      fileBottom: fileBox.bottom,
      rowHeight: rowBox.height,
    });
    if (Math.abs(nextOffset - this.selectedPageVisualOffsetPx) <= 0.25) {
      return selectedPageHasCrossedStickySlot;
    }

    this.selectedPageVisualOffsetPx = nextOffset;
    tree.style.setProperty('--tree-selected-page-visual-offset', `${nextOffset}px`);
    return selectedPageHasCrossedStickySlot;
  }

  private resetSelectedPageVisualOffset(tree: HTMLElement | null | undefined): void {
    this.selectedPageVisualOwner = null;
    this.selectedPageVisualOffsetPx = 0;
    tree?.style.setProperty('--tree-selected-page-visual-offset', '0px');
  }

  private updatePinnedRegionAncestorCount(count: number): void {
    if (count === this.pinnedRegionAncestorCount) return;
    this.pinnedRegionAncestorCount = count;
    this.cdr.markForCheck();
  }

  private updateStickyAncestorStackVisibility(visible: boolean): void {
    const visibilityChanged = visible !== this.stickyAncestorStackVisible;
    this.stickyAncestorStackVisible = visible;
    if (visible) {
      this.updateSemanticStickyScrollUnderOwner(null);
    } else {
      this.resetStickyScrollUnderState();
    }
    if (visibilityChanged) this.cdr.markForCheck();
  }

  /** Arrow keys control the same page disclosure state as pointer, Enter, and Space activation. */
  onPageDisclosureKeydown(page: ArchitecturalPlanPage, fileId: string, event: KeyboardEvent): void {
    const expanded = this.isRegionExpanded(page, fileId);
    const toggles =
      event.key === 'Enter' ||
      event.key === ' ' ||
      (event.key === 'ArrowRight' && !expanded) ||
      (event.key === 'ArrowLeft' && expanded);
    if (toggles) {
      event.preventDefault();
      event.stopPropagation();
      this.toggleRegionHierarchy(page, fileId, event);
    }
  }

  /** Whether a file's project-level connector segment is on the route-selected ancestry. */
  isFileBeforeActivePath(fileGroup: FileGroup): boolean {
    if (!this.hasActivePage()) {
      return false;
    }
    const activeIndex = this.fileGroups.findIndex(group => group.fileId === this.activeFileId);
    const fileIndex = this.fileGroups.findIndex(group => group.fileId === fileGroup.fileId);
    return activeIndex >= 0 && fileIndex >= 0 && fileIndex < activeIndex;
  }

  /** Whether a page's shared rail segment leads to the route-selected page in its file. */
  isPageBeforeActivePath(fileGroup: FileGroup, page: ArchitecturalPlanPage): boolean {
    if (fileGroup.fileId !== this.activeFileId || this.activePageNumber === null) {
      return false;
    }
    const indexes = this.pageIndexesFor(fileGroup.pages);
    const activeIndex = indexes.get(this.activePageNumber) ?? -1;
    const pageIndex = indexes.get(page.getPageNumber()) ?? -1;
    return activeIndex > 0 && pageIndex >= 0 && pageIndex < activeIndex;
  }

  /**
   * Each page's position in its file, by page number. Every row of the active file asks for its
   * own and the active page's position on every check; searching the list for both made a check
   * grow with the square of the file's page count (Issue #2997).
   */
  private pageIndexesFor(pages: ArchitecturalPlanPage[]): Map<number, number> {
    let indexes = this.pageIndexes.get(pages);
    if (!indexes) {
      indexes = new Map<number, number>();
      for (const [index, candidate] of pages.entries()) {
        const pageNumber = candidate.getPageNumber();
        if (!indexes.has(pageNumber)) indexes.set(pageNumber, index);
      }
      this.pageIndexes.set(pages, indexes);
    }
    return indexes;
  }

  onTreeKeydown(
    event: KeyboardEvent,
    kind: TreeItemKind,
    fileGroup?: FileGroup,
    page?: ArchitecturalPlanPage,
    region?: RegionHierarchyTreeNode.AsObject,
    regionFileId?: string
  ): void {
    const current = event.currentTarget as HTMLElement;
    const visibleItems = this.getVisibleTreeItems(current);
    const currentIndex = visibleItems.indexOf(current);
    let target: HTMLElement | undefined;

    switch (event.key) {
      case 'ArrowDown':
        target = visibleItems[Math.min(currentIndex + 1, visibleItems.length - 1)];
        break;
      case 'ArrowUp':
        target = visibleItems[Math.max(currentIndex - 1, 0)];
        break;
      case 'Home':
        target = visibleItems[0];
        break;
      case 'End':
        target = visibleItems[visibleItems.length - 1];
        break;
      case 'ArrowRight':
        if (kind === 'project' && !this.projectExpanded) {
          this.toggleProject();
        } else if (kind === 'file' && fileGroup && !fileGroup.expanded) {
          this.toggleFile(fileGroup, event);
        } else if (
          kind === 'page' &&
          page &&
          fileGroup &&
          this.powerUserMode &&
          !this.isRegionExpanded(page, fileGroup.fileId)
        ) {
          this.toggleRegionHierarchy(page, fileGroup.fileId, event);
        } else if (
          kind === 'region' &&
          page &&
          regionFileId &&
          region?.childNodesList?.length &&
          !this.isRegionNodeExpanded(region, page, regionFileId)
        ) {
          this.toggleRegionExpand(page, regionFileId, region, new MouseEvent('click'));
        } else {
          target = visibleItems[currentIndex + 1];
        }
        break;
      case 'ArrowLeft':
        if (kind === 'project' && this.projectExpanded) {
          this.toggleProject();
        } else if (kind === 'file' && fileGroup?.expanded) {
          this.toggleFile(fileGroup, event);
        } else if (
          kind === 'page' &&
          page &&
          fileGroup &&
          this.powerUserMode &&
          this.isRegionExpanded(page, fileGroup.fileId)
        ) {
          this.toggleRegionHierarchy(page, fileGroup.fileId, event);
        } else if (kind === 'page' && fileGroup) {
          target = this.findTreeItem(current, this.fileTreeKey(fileGroup.fileId));
        } else if (
          kind === 'region' &&
          page &&
          regionFileId &&
          region?.childNodesList?.length &&
          this.isRegionNodeExpanded(region, page, regionFileId)
        ) {
          this.toggleRegionExpand(page, regionFileId, region, new MouseEvent('click'));
        } else if (kind === 'region') {
          target = current.parentElement?.closest<HTMLElement>('[role="treeitem"]') ?? undefined;
        } else if (kind === 'file') {
          target = this.findTreeItem(current, 'project');
        }
        break;
      case 'Enter':
        if (kind === 'project') {
          this.openProjectSettings(event);
        } else if (kind === 'file' && fileGroup) {
          this.onFileLabelClick(fileGroup, event);
        } else if (kind === 'page' && fileGroup && page) {
          this.onPageClick(page, fileGroup.fileId);
        } else if (kind === 'region' && page && region) {
          this.onRegionClick(page, regionFileId || this.activeFileId || this.parseFileIdFromPath(page) || '1', region);
        }
        break;
      case ' ':
        if (kind === 'page' && fileGroup && page) {
          this.onCheckboxChange(page, fileGroup.fileId);
        } else if (kind === 'project') {
          break;
        } else if (kind === 'file' && fileGroup) {
          this.toggleFile(fileGroup, event);
        } else {
          return;
        }
        break;
      default:
        return;
    }

    event.preventDefault();
    event.stopPropagation();
    if (target) {
      target.focus();
    }
  }

  private getVisibleTreeItems(current: HTMLElement): HTMLElement[] {
    const tree = current.closest<HTMLElement>('[role="tree"]');
    return tree ? Array.from(tree.querySelectorAll<HTMLElement>('[role="treeitem"]')) : [];
  }

  private findTreeItem(current: HTMLElement, key: string): HTMLElement | undefined {
    const tree = current.closest<HTMLElement>('[role="tree"]');
    return tree?.querySelector<HTMLElement>(`[data-tree-key="${CSS.escape(key)}"]`) ?? undefined;
  }

  private requestActivePageReveal(): void {
    if (!this.hasActivePage()) {
      this.pendingActiveRevealKey = null;
      return;
    }
    this.pendingActiveRevealKey = `${this.activeFileId}:${this.activePageNumber}`;
    this.ensureActivePageAncestorsExpanded();
  }

  private requestActiveRegionReveal(): void {
    if (!this.powerUserMode || !this.activeRegionPath || !this.hasActivePage()) return;
    const activeFile = this.fileGroups.find(group => group.fileId === this.activeFileId);
    const activePage = activeFile?.pages.find(page => page.getPageNumber() === this.activePageNumber);
    if (!activeFile || !activePage) return;

    const key = this.regionCacheKey(activePage, activeFile.fileId);
    this.expandedRegionPages.add(key);
    const cached = this.regionHierarchyCache.get(key);
    if (cached) {
      const nodes = cached.map(node => node.toObject());
      this.expandActiveRegionAncestors(nodes, activePage, activeFile.fileId);
      this.restoreLinkedRegion(nodes);
    } else if (!this.loadingRegionHierarchy.has(key)) {
      void this.loadRegionHierarchy(activePage, activeFile.fileId, key);
    }
  }

  /** Reports the region the address bar names, once its node is known. */
  private restoreLinkedRegion(nodes: RegionHierarchyTreeNode.AsObject[]): void {
    const activeRegionPath = this.activeRegionPath;
    if (!activeRegionPath) return;
    const find = (candidates: RegionHierarchyTreeNode.AsObject[]): RegionHierarchyTreeNode.AsObject | null => {
      for (const node of candidates) {
        if (node.path === activeRegionPath) return node;
        if (activeRegionPath.startsWith(`${node.path}/`)) return find(node.childNodesList ?? []);
      }
      return null;
    };
    const region = find(nodes);
    if (region) this.regionRestored.emit(region);
  }

  private expandActiveRegionAncestors(
    nodes: RegionHierarchyTreeNode.AsObject[],
    page: ArchitecturalPlanPage,
    fileId: string
  ): void {
    const activeRegionPath = this.activeRegionPath;
    if (!activeRegionPath || !this.isActiveRegionPage(page, fileId)) return;
    const visit = (node: RegionHierarchyTreeNode.AsObject): boolean => {
      const ownsActivePath = activeRegionPath === node.path || activeRegionPath.startsWith(`${node.path}/`);
      if (!ownsActivePath) return false;
      if (node.childNodesList?.length && activeRegionPath !== node.path) {
        const expansionKey = this.regionNodeExpansionKey(page, fileId, node.path);
        if (node.depth === 0) this.expandedRegionNodes.delete(expansionKey);
        else this.expandedRegionNodes.add(expansionKey);
        node.childNodesList.some(visit);
      }
      return true;
    };
    nodes.some(visit);
  }

  private ensureActivePageAncestorsExpanded(): void {
    const activeRevealKey = `${this.activeFileId}:${this.activePageNumber}`;
    if (!this.hasActivePage() || this.pendingActiveRevealKey !== activeRevealKey) {
      return;
    }
    if (!this.projectExpanded) {
      this.projectExpanded = true;
      this.startProjectTreeTransition();
    }
    const activeFile = this.fileGroups.find(group => group.fileId === this.activeFileId);
    if (activeFile) {
      activeFile.expanded = true;
      this.focusedTreeItemKey = `page:${this.activeFileId}:${this.activePageNumber}`;
      this.pendingActiveRevealKey = null;
      this.persistExpansionState();
    }
  }

  hasActivePage(): boolean {
    return this.activeFileId !== null && this.activePageNumber !== null;
  }

  get disableNestedTreeAnimations(): boolean {
    return this.prefersReducedMotion || this.projectTreeAnimating;
  }

  private shouldFileStartExpanded(fileId: string): boolean {
    const saved = this.readExpansionState();
    return saved?.expandedFileIds.includes(fileId) ?? true;
  }

  private restoreExpansionState(): void {
    const saved = this.readExpansionState();
    this.projectExpanded = saved?.projectExpanded ?? true;
  }

  private readExpansionState(): { projectExpanded: boolean; expandedFileIds: string[] } | null {
    if (!this.planId) return null;
    try {
      const value = sessionStorage.getItem(`${DrawerComponent.SS_KEY_EXPANSION_PREFIX}${this.planId}`);
      if (!value) return null;
      const parsed: unknown = JSON.parse(value);
      const candidate = parsed as Record<string, unknown>;
      if (
        typeof parsed !== 'object' ||
        parsed === null ||
        typeof candidate['projectExpanded'] !== 'boolean' ||
        !Array.isArray(candidate['expandedFileIds']) ||
        !candidate['expandedFileIds'].every((fileId: unknown) => typeof fileId === 'string')
      ) {
        return null;
      }
      return {
        projectExpanded: candidate['projectExpanded'],
        expandedFileIds: candidate['expandedFileIds'] as string[],
      };
    } catch {
      return null;
    }
  }

  private persistExpansionState(): void {
    if (!this.planId) return;
    try {
      sessionStorage.setItem(
        `${DrawerComponent.SS_KEY_EXPANSION_PREFIX}${this.planId}`,
        JSON.stringify({
          projectExpanded: this.projectExpanded,
          expandedFileIds: this.fileGroups.filter(group => group.expanded).map(group => group.fileId),
        })
      );
    } catch {
      // Storage can be unavailable in hardened browsers; tree behavior still works for this render.
    }
  }

  toggleDrawer() {
    this.opened = !this.opened;
    this.openedChange.emit(this.opened);
  }

  closeDrawerOnHandset(): void {
    if (this.isHandset && this.opened) {
      this.opened = false;
      this.openedChange.emit(false);
      // Also reached after a navigation promise settles, not only from template events.
      this.cdr.markForCheck();
    }
  }

  /**
   * Handles clicks on page items:
   * - If a region is selected under this page → clear region, re-select page
   * - If page is already selected (no region) → toggle region hierarchy expand
   * - Otherwise → select the page
   */
  onPageClick(page: ArchitecturalPlanPage, fileId?: string): void {
    const isAlreadyActive = page.getPageNumber() === this.activePageNumber && (!fileId || fileId === this.activeFileId);

    if (isAlreadyActive && this.activeRegionPath) {
      this.activeRegionPath = null;
      this.onActivate(page, fileId);
      return;
    }

    if (isAlreadyActive && this.powerUserMode) {
      this.toggleRegionHierarchy(page, fileId || this.activeFileId || '1', new MouseEvent('click'));
      return;
    }

    this.onActivate(page, fileId);
  }

  onActivate(page: ArchitecturalPlanPage, fileId?: string) {
    // Navigation keeps its original fallback chain: a route needs a concrete
    // file id, so legacy pages fall back to the active file (or '1').
    const navigationFileId = fileId || this.parseFileIdFromPath(page) || this.activeFileId || '1';
    this.pageActivated.emit({ page, fileId: navigationFileId });
    this.closeDrawerOnHandset();
  }

  /**
   * Resolves the key a page's checked state is stored under.
   *
   * Prefers the caller-supplied id (the file group the page is rendered
   * under, sourced from `InputFileMetadata.getFileId()`), then the
   * `/files/{fileId}/pages/{n}` segment of the modern hierarchical folder path.
   *
   * Legacy pages carrying no file segment resolve to a sentinel rather than to
   * `activeFileId || '1'` as navigation does. Two reasons: the selection key
   * must be stable (`activeFileId` changes as the user navigates, which would
   * silently move checked pages between buckets), and inventing a plausible-
   * looking id is precisely what let Issue #1652 mis-attribute pages. The
   * sentinel matches no real file, so `groupSelectionByFile()` is forced to
   * decide explicitly — single input file, or skip and warn.
   */
  private resolveFileIdForPage(page: ArchitecturalPlanPage, fileId?: string): string {
    return fileId || this.parseFileIdFromPath(page) || DrawerComponent.UNRESOLVED_FILE_ID;
  }

  /**
   * Memoizing wrapper around the shared `extractFileIdFromPage()` util, which
   * returns the file id from a page's modern hierarchical folder path or
   * `null` for legacy paths carrying no `/files/{id}/` segment.
   *
   * The shared util is used rather than a local regex so non-numeric file ids
   * resolve correctly. `isPageChecked()` is template-bound, so this runs on
   * every change detection cycle for every rendered page; the result depends
   * only on the page's own immutable folder path, so it is memoized in a
   * WeakMap — entries are collected with the page objects when a plan reloads.
   */
  private parseFileIdFromPath(page: ArchitecturalPlanPage): string | null {
    const cached = this.parsedFileIdCache.get(page);
    if (cached !== undefined) {
      return cached;
    }

    const parsedFileId = extractFileIdFromPage(page);
    this.parsedFileIdCache.set(page, parsedFileId);
    return parsedFileId;
  }

  /** Returns a stable key for the region hierarchy cache. */
  private regionCacheKey(page: ArchitecturalPlanPage, fileId: string): string {
    // Built for every page row several times per check (Issue #2997), so remembered per page.
    let keys = this.regionCacheKeys.get(page);
    if (!keys) {
      keys = new Map<string, string>();
      this.regionCacheKeys.set(page, keys);
    }
    let key = keys.get(fileId);
    if (key === undefined) {
      key = `${fileId}:${page.getPageNumber()}`;
      keys.set(fileId, key);
    }
    return key;
  }

  /** Toggles the region hierarchy sub-items for a page in the TOC. */
  toggleRegionHierarchy(page: ArchitecturalPlanPage, fileId: string, event: Event): void {
    event.stopPropagation();
    const key = this.regionCacheKey(page, fileId);

    if (this.expandedRegionPages.has(key)) {
      this.setRegionHierarchyInteractive(page, fileId, false);
      this.expandedRegionPages.delete(key);
      if (fileId === this.activeFileId && page.getPageNumber() === this.activePageNumber) {
        this.synchronizeStickyExpansionProjection(`page:${key}`, true);
      }
      return;
    }

    this.expandedRegionPages.add(key);
    this.setRegionHierarchyInteractive(page, fileId, true);
    if (fileId === this.activeFileId && page.getPageNumber() === this.activePageNumber) {
      this.synchronizeStickyExpansionProjection(`page:${key}`, false);
    }

    if (!this.regionHierarchyCache.has(key)) {
      this.loadRegionHierarchy(page, fileId, key);
    }
  }

  /** Makes an animated page-region subtree inert before its leaving view can retain stale bindings. */
  private setRegionHierarchyInteractive(page: ArchitecturalPlanPage, fileId: string, interactive: boolean): void {
    const pageNumber = String(page.getPageNumber());
    const pageItem = Array.from(this.projectTreeElement?.querySelectorAll<HTMLElement>('.page-treeitem') ?? []).find(
      element => element.dataset['fileId'] === fileId && element.dataset['pageNumber'] === pageNumber
    );
    const group = pageItem?.querySelector<HTMLElement>(':scope > .region-hierarchy-container');
    if (!group) return;

    group.toggleAttribute('inert', !interactive);
    if (interactive) {
      group.removeAttribute('aria-hidden');
      group.style.removeProperty('pointer-events');
    } else {
      group.setAttribute('aria-hidden', 'true');
      group.style.pointerEvents = 'none';
    }
  }

  isRegionExpanded(page: ArchitecturalPlanPage, fileId: string): boolean {
    return this.expandedRegionPages.has(this.regionCacheKey(page, fileId));
  }

  isRegionLoading(page: ArchitecturalPlanPage, fileId: string): boolean {
    return this.loadingRegionHierarchy.has(this.regionCacheKey(page, fileId));
  }

  getRegionNodes(page: ArchitecturalPlanPage, fileId: string): RegionHierarchyTreeNode.AsObject[] {
    const nodes = this.regionHierarchyCache.get(this.regionCacheKey(page, fileId));
    if (!nodes) return DrawerComponent.NO_REGION_NODES;
    // The template asks for an expanded page's nodes on every check. Converting the whole tree
    // each time was costly and handed the tree new objects each time (Issue #2997), so the
    // conversion is kept for as long as the cache holds the same response.
    let objects = this.regionNodeObjects.get(nodes);
    if (!objects) {
      objects = nodes.map(n => n.toObject());
      this.regionNodeObjects.set(nodes, objects);
    }
    return objects;
  }

  onRegionClick(page: ArchitecturalPlanPage, fileId: string, region: RegionHierarchyTreeNode.AsObject): void {
    this.activeRegionPath = region.path;
    this.collapsedStickyRegionCounts.clear();
    this.pinnedRegionAncestorCount = 0;
    this.updateStickyAncestorStackVisibility(false);
    this.regionActivated.emit({ page, fileId, region });
    this.scheduleBottomActiveProjectionUpdate();
    this.closeDrawerOnHandset();
  }

  onRegionHover(region: RegionHierarchyTreeNode.AsObject | null): void {
    this.regionHovered.emit(region);
  }

  /** Returns the NCS discipline name for a sheet ID prefix (e.g. "A5.0" → "Architectural"). */
  getSheetDiscipline(sheetId: string): string {
    if (!sheetId) return '';
    const prefix = sheetId.charAt(0).toUpperCase();
    return DrawerComponent.DISCIPLINE_MAP[prefix] || '';
  }

  /** Whether a given region is the currently active one. */
  isActiveRegion(region: RegionHierarchyTreeNode.AsObject, page: ArchitecturalPlanPage, fileId: string): boolean {
    return this.isActiveRegionPage(page, fileId) && this.activeRegionPath === region.path;
  }

  isRegionActiveAncestor(
    region: RegionHierarchyTreeNode.AsObject,
    page: ArchitecturalPlanPage,
    fileId: string
  ): boolean {
    return (
      this.isActiveRegionPage(page, fileId) &&
      !!this.activeRegionPath &&
      this.activeRegionPath.startsWith(`${region.path}/`)
    );
  }

  /** Keeps the selected ancestry trunk blue only until the sibling that owns the active path. */
  isRegionBeforeActivePath(
    nodes: RegionHierarchyTreeNode.AsObject[],
    index: number,
    page: ArchitecturalPlanPage,
    fileId: string
  ): boolean {
    const activePath = this.activeRegionPath;
    if (!this.isActiveRegionPage(page, fileId) || !activePath || index < 0 || index >= nodes.length) return false;
    const activeIndex = nodes.findIndex(node => activePath === node.path || activePath.startsWith(`${node.path}/`));
    return activeIndex > index;
  }

  private isActiveRegionPage(page: ArchitecturalPlanPage, fileId: string): boolean {
    return fileId === this.activeFileId && page.getPageNumber() === this.activePageNumber;
  }

  regionAccessibleName(region: RegionHierarchyTreeNode.AsObject): string {
    const title = region.title || region.slug;
    return region.ncsClassification && region.ncsClassification !== title
      ? `${title}, ${region.ncsClassification}`
      : title;
  }

  /** Toggle expand/collapse of an individual region node's children. Shift+click expands the full branch. */
  toggleRegionExpand(
    page: ArchitecturalPlanPage,
    fileId: string,
    region: RegionHierarchyTreeNode.AsObject,
    event: MouseEvent
  ): void {
    event.stopPropagation();
    const wasExpanded = this.isRegionNodeExpanded(region, page, fileId);

    if (event.shiftKey) {
      this.toggleBranchRecursive(page, fileId, region);
      this.setRegionChildrenInteractive(page, fileId, region, !wasExpanded);
      this.synchronizeStickyExpansionProjection(`region:${region.path}`, wasExpanded);
      return;
    }

    const expansionKey = this.regionNodeExpansionKey(page, fileId, region.path);
    if (this.expandedRegionNodes.has(expansionKey)) {
      this.expandedRegionNodes.delete(expansionKey);
    } else {
      this.expandedRegionNodes.add(expansionKey);
    }
    this.setRegionChildrenInteractive(page, fileId, region, !wasExpanded);
    this.synchronizeStickyExpansionProjection(`region:${region.path}`, wasExpanded);
  }

  /** Makes a leaving nested region group inert before Angular removes its connected view. */
  private setRegionChildrenInteractive(
    page: ArchitecturalPlanPage,
    fileId: string,
    region: RegionHierarchyTreeNode.AsObject,
    interactive: boolean
  ): void {
    const pageItem = Array.from(this.projectTreeElement?.querySelectorAll<HTMLElement>('.page-treeitem') ?? []).find(
      element => element.dataset['fileId'] === fileId && Number(element.dataset['pageNumber']) === page.getPageNumber()
    );
    const regionItem = Array.from(
      pageItem?.querySelectorAll<HTMLElement>('.region-list-item[data-region-path]') ?? []
    ).find(element => element.dataset['regionPath'] === region.path);
    const group = regionItem?.querySelector<HTMLElement>(':scope > .region-children-container');
    if (!group) return;

    group.toggleAttribute('inert', !interactive);
    if (interactive) {
      group.removeAttribute('aria-hidden');
      group.style.removeProperty('pointer-events');
    } else {
      group.setAttribute('aria-hidden', 'true');
      group.style.pointerEvents = 'none';
    }
  }

  /** Recursively expand or collapse an entire branch. */
  private toggleBranchRecursive(
    page: ArchitecturalPlanPage,
    fileId: string,
    region: RegionHierarchyTreeNode.AsObject
  ): void {
    const isCurrentlyExpanded = this.isRegionNodeExpanded(region, page, fileId);
    const visit = (node: RegionHierarchyTreeNode.AsObject) => {
      if (!node.childNodesList?.length) return;
      const expansionKey = this.regionNodeExpansionKey(page, fileId, node.path);
      if (isCurrentlyExpanded) {
        if (node.depth === 0) this.expandedRegionNodes.add(expansionKey);
        else this.expandedRegionNodes.delete(expansionKey);
      } else {
        if (node.depth === 0) this.expandedRegionNodes.delete(expansionKey);
        else this.expandedRegionNodes.add(expansionKey);
      }
      for (const child of node.childNodesList) {
        visit(child);
      }
    };
    visit(region);
  }

  /** Whether a region node's children are expanded. Only depth-0 nodes are expanded by default. */
  isRegionNodeExpanded(region: RegionHierarchyTreeNode.AsObject, page: ArchitecturalPlanPage, fileId: string): boolean {
    const expansionKey = this.regionNodeExpansionKey(page, fileId, region.path);
    if (region.depth === 0) {
      return !this.expandedRegionNodes.has(expansionKey);
    }
    return this.expandedRegionNodes.has(expansionKey);
  }

  private regionNodeExpansionKey(page: ArchitecturalPlanPage, fileId: string, path: string): string {
    return `${fileId}:${page.getPageNumber()}:${path}`;
  }

  private async loadRegionHierarchy(page: ArchitecturalPlanPage, fileId: string, key: string): Promise<void> {
    this.loadingRegionHierarchy.add(key);
    this.cdr.detectChanges();

    try {
      const response: GetRegionHierarchyResponse = await this.planReviewerService.getRegionHierarchy(
        this.planId,
        page.getPageNumber(),
        fileId
      );

      if (response.getHasHierarchy()) {
        this.regionHierarchyCache.set(key, response.getNodesList());
        const nodes = response.getNodesList().map(node => node.toObject());
        this.expandActiveRegionAncestors(nodes, page, fileId);
        // Only for the page on screen: a hierarchy loaded for another page that the reviewer
        // expanded must not be searched for the active page's region path.
        if (page.getPageNumber() === this.activePageNumber && fileId === this.activeFileId) {
          this.restoreLinkedRegion(nodes);
        }
      } else {
        this.regionHierarchyCache.set(key, []);
      }
    } catch (error) {
      ClientLoggingService.log('ERROR', `[DrawerComponent] Failed to load region hierarchy for ${key}:`, error);
      this.regionHierarchyCache.set(key, []);
    } finally {
      this.loadingRegionHierarchy.delete(key);
      this.cdr.detectChanges();
    }
  }

  /**
   * Whether a page is checked *within its own file*.
   *
   * Issue #1652: page numbers repeat across files, so the file id is part of
   * the identity — checking page 5 of one file must not tick page 5 of another.
   */
  isPageChecked(page: ArchitecturalPlanPage, fileId?: string): boolean {
    const resolvedFileId = this.resolveFileIdForPage(page, fileId);
    return this.checkedPagesByFile.get(resolvedFileId)?.has(page.getPageNumber()) ?? false;
  }

  onCheckboxChange(page: ArchitecturalPlanPage, fileId?: string) {
    const resolvedFileId = this.resolveFileIdForPage(page, fileId);
    const pageNumber = page.getPageNumber();

    // Rebuild rather than mutate: `checkedPagesByFile` is an @Input(), so a new
    // reference (map and inner set) is what lets an OnPush / reference-equality
    // parent see the change.
    const next = new Map(this.checkedPagesByFile);
    const pagesForFile = new Set(next.get(resolvedFileId) ?? []);

    if (pagesForFile.has(pageNumber)) {
      pagesForFile.delete(pageNumber);
    } else {
      pagesForFile.add(pageNumber);
    }

    if (pagesForFile.size === 0) {
      next.delete(resolvedFileId);
    } else {
      next.set(resolvedFileId, pagesForFile);
    }

    this.checkedPagesByFile = next;

    // Emit the updated selection map
    this.checkedPagesChanged.emit(this.checkedPagesByFile);

    // Update the selectedPages array to trigger change detection
    this.updateSelectedPages();

    // Reveal the handset toolbar after a selection instead of leaving it underneath the open drawer.
    if (this.checkedPagesByFile.size > 0) {
      this.closeDrawerOnHandset();
    }
  }

  /** Total number of checked pages across every file. */
  get checkedPageCount(): number {
    let total = 0;
    for (const pageNumbers of this.checkedPagesByFile.values()) {
      total += pageNumbers.size;
    }
    return total;
  }

  /**
   * Rebuilds `selectedPages` as `{ fileId, page }` pairs.
   *
   * The per-file `fileGroups` mapping is authoritative when available; the flat
   * `pages` list (legacy projects with no file metadata) falls back to the
   * resolver so each entry still carries a file id.
   */
  private updateSelectedPages(): void {
    const candidates: SelectedPageRef[] =
      this.fileGroups.length > 0
        ? this.fileGroups.flatMap(group => group.pages.map(page => ({ fileId: group.fileId, page })))
        : this.pages.map(page => ({ fileId: this.resolveFileIdForPage(page), page }));

    const seen = new Set<string>();
    this.selectedPages = candidates.filter(ref => {
      const key = `${ref.fileId}:${ref.page.getPageNumber()}`;
      if (seen.has(key) || !this.checkedPagesByFile.get(ref.fileId)?.has(ref.page.getPageNumber())) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }

  onSelectedPagesAction(action: SelectedPagesToolbarAction): void {
    ClientLoggingService.log('INFO', '[DrawerComponent] Selected pages action:', action);

    if (action.type === 'reingest') {
      this.handleReingestPages(action.pages || []);
    } else if (action.type === 'cancel' || action.type === 'clear-selection') {
      this.clearAllSelections();
    }
  }

  private clearAllSelections(): void {
    // Clear all checkbox selections with a fresh reference rather than an
    // in-place clear(), so parents binding by reference observe the change.
    this.checkedPagesByFile = new Map();
    this.checkedPagesChanged.emit(this.checkedPagesByFile);

    // Update the selectedPages array to trigger toolbar visibility update
    this.updateSelectedPages();
    // Runs after the re-ingest dialogs close, outside any template event.
    this.cdr.markForCheck();
  }

  /**
   * Opens a re-ingestion dialog per selected file.
   *
   * Issue #1652: previously this hardcoded `fileMetadataList[0]` as "the file"
   * for the whole request, so in a multi-file project the dialog showed the
   * page numbers of one file next to the name, page count and ingested-pages
   * line of another — and could dispatch ingestion against the wrong file.
   * The selection now carries a `fileId` per page, so pages are grouped by
   * their own file and each group gets a dialog scoped to that file.
   */
  private async handleReingestPages(selection: SelectedPageRef[]): Promise<void> {
    try {
      ClientLoggingService.log(
        'INFO',
        '[DrawerComponent] Handling re-ingest for pages:',
        selection.map(ref => `${ref.fileId}:${ref.page.getPageNumber()}`)
      );

      if (selection.length === 0) {
        this.snackBar.open('No pages selected for re-ingestion', 'Close', { duration: 3000 });
        return;
      }

      // Get the input file metadata for this project
      const fileMetadataList = await this.getProjectFileMetadata();

      if (!fileMetadataList || fileMetadataList.length === 0) {
        this.snackBar.open('No input files found for this project', 'Close', { duration: 3000 });
        return;
      }

      const { groups, unresolvedPageCount } = this.groupSelectionByFile(selection, fileMetadataList);

      if (unresolvedPageCount > 0) {
        // Never silently drop a selected page — tell the user which ones could
        // not be matched to an input file rather than re-targeting them.
        this.snackBar.open(
          `Could not find the input file for ${unresolvedPageCount} selected page(s); they were skipped`,
          'Close',
          { duration: 5000 }
        );
      }

      if (groups.length === 0) {
        this.snackBar.open('Could not find input file for re-ingestion', 'Close', { duration: 3000 });
        return;
      }

      // Dialogs are opened one file at a time so stacked dialogs never hide
      // each other, and so the user can see which file each request targets.
      let startedPageCount = 0;
      for (const group of groups) {
        const result = await this.openReingestDialogForFile(group.file, group.pageNumbers);
        if (result && result.action === 'ingest' && result.started) {
          ClientLoggingService.log(
            'INFO',
            `[DrawerComponent] Page re-ingestion started for file ${group.file.getFileId()}:`,
            result
          );
          startedPageCount += group.pageNumbers.length;
        }
      }

      if (startedPageCount === 0) {
        return;
      }

      // Clear the selections after starting re-ingestion
      this.clearAllSelections();

      this.snackBar
        .open(
          `Re-ingestion started for ${startedPageCount} page(s). Check the Tasks page for progress.`,
          'View Tasks',
          { duration: 5000 }
        )
        .onAction()
        .subscribe(() => {
          this.router.navigate(['/projects', this.planId, 'tasks']);
        });
    } catch (error) {
      ClientLoggingService.log('ERROR', '[DrawerComponent] Error handling re-ingest:', error);
      this.snackBar.open('Error initiating page re-ingest', 'Close', { duration: 3000 });
    }
  }

  /**
   * Buckets the selected pages by the input file they actually belong to.
   *
   * Groups are emitted in `fileMetadataList` order for a stable dialog
   * sequence. Pages whose file cannot be resolved are counted rather than
   * reassigned, so a mismatch surfaces to the user instead of dispatching
   * ingestion against the wrong file.
   */
  private groupSelectionByFile(
    selection: SelectedPageRef[],
    fileMetadataList: InputFileMetadata[]
  ): { groups: { file: InputFileMetadata; pageNumbers: number[] }[]; unresolvedPageCount: number } {
    const pageNumbersByFileId = new Map<string, Set<number>>();
    for (const ref of selection) {
      const pageNumbers = pageNumbersByFileId.get(ref.fileId) ?? new Set<number>();
      pageNumbers.add(ref.page.getPageNumber());
      pageNumbersByFileId.set(ref.fileId, pageNumbers);
    }

    const groups: { file: InputFileMetadata; pageNumbers: number[] }[] = [];
    let unresolvedPageCount = 0;

    for (const [fileId, pageNumbers] of pageNumbersByFileId) {
      const file =
        fileMetadataList.find(metadata => metadata.getFileId() === fileId) ??
        // Legacy single-file projects have page paths with no `/files/{id}/`
        // segment, so their pages carry the UNRESOLVED_FILE_ID sentinel. With
        // exactly one input file there is no ambiguity about where they belong;
        // with several, the loop below skips and warns rather than guessing.
        (fileMetadataList.length === 1 ? fileMetadataList[0] : undefined);

      if (!file) {
        ClientLoggingService.log(
          'WARN',
          `[DrawerComponent] No input file metadata for fileId ${fileId}; skipping pages:`,
          [...pageNumbers]
        );
        unresolvedPageCount += pageNumbers.size;
        continue;
      }

      const existing = groups.find(group => group.file === file);
      if (existing) {
        existing.pageNumbers = [...new Set([...existing.pageNumbers, ...pageNumbers])].sort((a, b) => a - b);
      } else {
        groups.push({ file, pageNumbers: [...pageNumbers].sort((a, b) => a - b) });
      }
    }

    groups.sort((a, b) => fileMetadataList.indexOf(a.file) - fileMetadataList.indexOf(b.file));
    return { groups, unresolvedPageCount };
  }

  /** Opens the ingestion dialog scoped to one file, pre-filled with that file's selected pages. */
  private openReingestDialogForFile(
    file: InputFileMetadata,
    pageNumbers: number[]
  ): Promise<ReingestDialogResult | undefined> {
    const dialogData: PageIngestionDialogData = {
      file,
      projectId: this.planId,
      // "N of M pages ingested" must describe this file, not the selection size.
      ingestedPageCount: file.getExtractedPagesList()?.length ?? 0,
    };

    const dialogRef = this.handsetDialog.open(PageIngestionDialogComponent, {
      width: '600px',
      maxWidth: '90vw',
      data: dialogData,
      disableClose: false,
    });

    // Pre-populate the dialog with this file's selected page numbers
    dialogRef.afterOpened().subscribe(() => {
      const instance = dialogRef.componentInstance;
      instance.pages = 'custom';
      instance.customPages = pageNumbers.join(',');
    });

    // Resolved manually (rather than via firstValueFrom) so a dialog stream that
    // completes without emitting resolves to `undefined` instead of rejecting.
    return new Promise<ReingestDialogResult | undefined>(resolve => {
      dialogRef.afterClosed().subscribe({
        next: result => resolve(result),
        error: () => resolve(undefined),
        complete: () => resolve(undefined),
      });
    });
  }

  private async getProjectFileMetadata(): Promise<InputFileMetadata[]> {
    try {
      // Use the FileMetadataService to get the list of input files for this project
      const response = await this.fileMetadataService.listFileMetadata(this.planId).toPromise();
      return response?.getFilesList() || [];
    } catch (error) {
      ClientLoggingService.log('ERROR', '[DrawerComponent] Error getting file metadata:', error);
      return [];
    }
  }

  trackByPageNumber(index: number, page: ArchitecturalPlanPage) {
    return page.getPageNumber();
  }
}
